import {
  ApplyToLevel,
  ApplyToProductType,
  AttractionQRCodeSendingStatus,
  ContactStatus,
  CouponCreateType,
  CouponCustomerInfoType,
  CreateEventStep,
  DiscountType,
  EventType,
  FieldObjectType,
  FrequencyOfSellType,
  GenerateCharacterPosition,
  IAttendantTemplateItem,
  OrderStatusKey,
  OrderType,
  ParticipantImportSource,
  PaymentMethodKey,
  PaymentStatusType,
  PickupAccountType,
  PickupType,
  PriceType,
  PrivateFlowType,
  PrivateFlowVerifyType,
  QRCodeType,
  ReadonlyPaymentMethodkey,
  SeatLayoutPosition,
  SeatLayoutStatus,
  SeatLayoutType,
  SupportedCurrency,
  SupportedLanguage,
  TicketPlatform,
} from "interfaces";
import {
  BLOOD_TYPE_OBJECT,
  COUNTRY_OBJECT,
  DATE_FIELDS,
  FINISHER_TSHIRT_SIZE_ARRAY,
  GENDER_OBJECT,
  NATIONALITIES_OBJECT,
  PROVINCE_OBJECT,
  RELATIONSHIP_OBJECT,
  SELECT_FIELDS,
  TEXT_FIELDS,
  TSHIRT_SIZE_ARRAY,
} from "./templateFields";
import i18n from "./i18n";

// contain at least one digit, one lowercase letter, one uppercase letter, one special character, and be between 6 and 72 characters long.
export const REGEX_STRONG_PASSWORD =
  /^(?=.*[A-Z])(?=.*[!@#$&*])(?=.*[0-9])(?=.*[a-z]).{6,72}$/;

// export const REGEX_VALID_EMAIL = /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*(\.[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*)*\.[a-zA-Z]{2,}$/;
export const REGEX_VALID_EMAIL = /^(?:(?:(?:(?:[a-zA-Z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(?:\.([a-zA-Z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|(?:(?:\x22)(?:(?:(?:(?:\x20|\x09)*(?:\x0d\x0a))?(?:\x20|\x09)+)?(?:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(?:(?:[\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(?:(?:(?:\x20|\x09)*(?:\x0d\x0a))?(\x20|\x09)+)?(?:\x22))))@(?:(?:(?:[a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(?:(?:[a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])(?:[a-zA-Z]|\d|-|\.|\~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*(?:[a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(?:[a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])(?:[a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+$/;

export const REGEX_PHONE_NUMBER =
  /\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{4})/;

// Use for ticket: AU-2068
export const REGEX_PHONE_NUMBER_MIN_5 =
  /\(?([0-9]{3})\)?([ .-]?)([0-9]{2,})(?:\2([0-9]+))?/;

export const REGEX_DOMAIN =
  /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/; //eslint-disable-line

export const REGEX_VALID_TEMPLATE_KEY = /^[a-zA-Z0-9]+(?:_[a-zA-Z0-9]+)*$/;

export const REGEX_NUMBER = /^[0-9\b]+$/;

export const DEFAULT_PAGE_SIZE = 10;

export const STATUS_OPTIONS = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

export const EWAIVER_STATUS = [
  { value: "true", label: "Signed" },
  { value: "false", label: "Unsigned" },
];

export const SELF_PICK_STATUS = [
  { value: true, label: "Self-pickup" },
  { value: false, label: "Authorized Pickup" },
];

export const PARTICIPANT_IMPORT_SOURCES = [
  { value: ParticipantImportSource.SYSTEM, label: "System" },
  { value: ParticipantImportSource.IMPORT, label: "Import" },
];

export const BLOG_STATUS_OPTION = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export const EVENT_ADDON_TYPES = [
  { value: "simple", label: "Simple" },
  { value: "options", label: "Options" },
];

export const EVENT_ADDON_SCOPES = [
  { value: "event", label: "All tickets" },
  { value: "ticket", label: "Specific tickets" },
];

export const EVENT_ADDON_QUANTITY_MODES = [
  { value: "single", label: "Choose 1" },
  { value: "multi_free", label: "Free qty" },
  { value: "multi_fixed", label: "Fixed qty" },
  { value: "total_cap", label: "Total cap" },
];

export const EVENT_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "on_selling", label: "On Selling" },
  { value: "coming_soon", label: "Coming Soon" },
  { value: "closed", label: "Closed" },
];

export const EVENT_STEP_STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "published", label: "Published" },
  { value: "un_published", label: "Unpublished" },
  { value: "draft", label: "Draft" },
];

export const EVENT_STEP_STATUS_OPTIONS_BY_LOCALE = [
  { value: "", label: i18n.t("all-status") },
  { value: "published", label: i18n.t("published") },
  { value: "un_published", label: i18n.t("unpublished") },
  { value: "draft", label: i18n.t("draft") },
]

export const EVENT_TYPE_OPTIONS = [
  { value: EventType.SPORT, label: "Sport" },
  { value: EventType.ATTRACTION, label: "Attraction" },
];

export const FIPIX_EVENT_HIDDEN_STATUS = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export const BOOLEAN_STATUS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export const COMPLETED_STATUS = [
  { value: "true", label: "Completed" },
  { value: "false", label: "Incomplete" },
];

export const PICKUP_STATUS = COMPLETED_STATUS;

export const PAYMENT_METHOD_FREE = [
  { value: ReadonlyPaymentMethodkey.FREE, label: "Free" },
  { value: ReadonlyPaymentMethodkey.VOUCHER, label: "Voucher" },
];

export const ORDER_PAYMENT_METHODS = [
  { value: PaymentMethodKey.ATM, label: "ATM" },
  { value: PaymentMethodKey.CREDIT, label: "Credit" },
  { value: PaymentMethodKey.BANK_TRANSFER, label: "Bank Transfer" },
  { value: PaymentMethodKey.COD, label: "COD" },
  { value: PaymentMethodKey.PAYOO, label: "PAYOO" },
  { value: PaymentMethodKey.ZALOPAY, label: "ZaloPay" },
  { value: PaymentMethodKey.MOMO, label: "Momo" },
  { value: PaymentMethodKey.VIETQR, label: "VietQR" },
  { value: PaymentMethodKey.VNPAY, label: "VNPAY" },
  { value: PaymentMethodKey.BNPL, label: "BNPL" },
  { value: PaymentMethodKey.VIETTEL_MONEY_VIETQR, label: "Viettel Money - VietQR" },
  { value: PaymentMethodKey.VIETTEL_MONEY_ATM, label: "Viettel Money - ATM" },
  { value: PaymentMethodKey.VIETTEL_MONEY_CREDIT, label: "Viettel Money - Credit" },
  { value: PaymentMethodKey.PAYOO_ATM, label: "Payoo - Domestic card" },
  { value: PaymentMethodKey.PAYOO_CREDIT, label: "Payoo - Credit/Debit card" },
  { value: PaymentMethodKey.ONEPAY_VIETQR, label: "OnePay - VietQR" },
]

export const CONTENT_ORDER_PAYMENT_METHODS = ORDER_PAYMENT_METHODS.concat([
  { value: PaymentMethodKey.STRIPE, label: "Pay via Stripe" },
]);

export const PICKUP_ACCOUNT_TYPES = [
  { value: PickupAccountType.POS, label: "POS" },
  { value: PickupAccountType.ADMIN, label: "Admin" },
];

export const ACCOUNT_PICK_TYPES = [
  { value: PickupType.ALL, label: "All" },
  { value: PickupType.SELF_PICK, label: "Self-Pick" },
  { value: PickupType.GROUP_PICK, label: "Group-Pick" },
];

export const PRIVATE_FLOW_VERIFY_TYPES = [
  {
    value: PrivateFlowVerifyType.PASSCODE,
    label: i18n.t("select.verifyType.passcode"),
  },
  {
    value: PrivateFlowVerifyType.SPECIFIC_EMAIL,
    label: i18n.t("select.verifyType.specificEmail"),
  },
  {
    value: PrivateFlowVerifyType.DOMAIN_EMAIL,
    label: i18n.t("select.verifyType.emailDomain"),
  },
  {
    value: PrivateFlowVerifyType.PASSCODE_AND_EMAIL,
    label: i18n.t("select.verifyType.passcodeAndSpecificEmail"),
  },
];

export const PRIVATE_FLOW_TYPES = [
  {
    value: PrivateFlowType.PRIVATE_FLOW,
    label: "Private flow",
  },
  {
    value: PrivateFlowType.ACTIUP_PRIVATE_FLOW,
    label: "ActiUp private flow",
  },
];

export const PAYMENT_METHODS = [{ value: PaymentMethodKey.ALL, label: "All" }].concat(ORDER_PAYMENT_METHODS);

export const USD_PAYMENT_METHODS = [{value: PaymentMethodKey.ALL, label: "All"}].concat([
  {value: PaymentMethodKey.CREDIT, label: "Credit"},
]);

export const ADMIN_USD_PAYMENT_METHODS = [{value: PaymentMethodKey.ALL, label: "All"}].concat([
  {value: PaymentMethodKey.CREDIT, label: "Credit"},
  {value: PaymentMethodKey.STRIPE, label: "Stripe"},
]);

export const DEFAULT_PAYMENT_METHOD_VALUE = [
  PaymentMethodKey.ATM,
  PaymentMethodKey.CREDIT,
  PaymentMethodKey.PAYOO,
  PaymentMethodKey.VIETQR,
  PaymentMethodKey.VNPAY,
  PaymentMethodKey.BNPL,
];

export const DEFAULT_USD_CURRENCY_PAYMENT_METHODS_VALUE = [];

export const ORDER_STATUS = [
  {
    value: OrderStatusKey.PENDING,
    label: "Pending payment",
  },
  {
    value: OrderStatusKey.SUCCESSFUL,
    label: "Order success",
  },
  {
    value: OrderStatusKey.FAILED,
    label: "Canceled",
  },
  {
    value: OrderStatusKey.REFUNDED,
    label: "Refund",
  },
  {
    value: OrderStatusKey.WAITING_FOR_REFUND,
    label: "Waiting for refund",
  },
];

export const ORDER_TYPES = [
  {
    value: OrderType.VIRTUAL_EVENT,
    label: "Individual ticket",
  },
  {
    value: OrderType.GROUP_BUY,
    label: "Group ticket",
  },
  {
    value: OrderType.ATTRACTIONS,
    label: "Attraction ticket",
  },
  {
    value: OrderType.CHANGE_DISTANCE,
    label: "Change distance",
  },
  {
    value: OrderType.TRANSFER_TICKET,
    label: "Transfer ticket",
  },
];

export const ORDER_IS_VAT = [
  {
    value: "true",
    label: "Yes",
  },
  {
    value: "false",
    label: "No",
  },
];

export const PAYMENT_STATUS_LIST = [
  {
    value: PaymentStatusType.NEW,
    label: "Proccessing",
  },
  {
    value: PaymentStatusType.SUCCESS,
    label: "Payment Success",
  },
  {
    value: PaymentStatusType.REVIEW,
    label: "Payment Review",
  },
  {
    value: PaymentStatusType.FAIL,
    label: "Payment Failure",
  },
  {
    value: PaymentStatusType.REFUND,
    label: "Refund",
  },
  {
    value: PaymentStatusType.REFUND_REVIEW,
    label: "Refund Review",
  },
];

export const EDIT_ORDER_STATUS_LIST = [
  {
    value: OrderStatusKey.SUCCESSFUL,
    label: "Order success",
  },
  {
    value: OrderStatusKey.FAILED,
    label: "Canceled",
  },
  {
    value: OrderStatusKey.REFUNDED,
    label: "Refund",
  },
];

export const WEBSITE_HOST_NAME = "actiup.net";

export const TEXT_INPUT_VALIDATIONS = [
  { value: "length", label: i18n.t("validation.fieldType.length") },
  { value: "phone", label: i18n.t("validation.fieldType.phone") },
  { value: "email", label: i18n.t("validation.fieldType.email") }
];

export const SELLING_TYPE = [
  { value: "multiple", label: i18n.t("select.sellingType.selectMultipleTicketTypes") },
  { value: "one_type", label: i18n.t("select.sellingType.selectOneTicketType") }
];

export const FREQUENCY_OF_SELL_TYPES = [
  { value: FrequencyOfSellType.ONCE, label: "One-time sale event" },
  { value: FrequencyOfSellType.USUALLY, label: "Regular selling event" },
  { value: FrequencyOfSellType.SEATMAP, label: "Seatmap" }
];

export const CONTACT_STATUS = [
  { value: ContactStatus.UNRESOLVED, label: "Unresolved" },
  { value: ContactStatus.PROCESSING, label: "Processing" },
  { value: ContactStatus.RESOLVED, label: "Resolved" },
];

export const BLOG_SUB_CATEGORY_TYPE = [
  { value: "promotion", label: "Promotion" },
  { value: "news", label: "News" },
  { value: "blogs", label: "Blogs" },
  { value: "health", label: "Health & Wellness" },
  { value: "other", label: "Other" }
];

export const PRICE_TYPES = [
  { value: PriceType.PAID, label: i18n.t("select.minPrice.paid") },
  { value: PriceType.FREE, label: i18n.t("select.minPrice.free") },
];

export const AUTO_SEATING_CHARACTER_POSITIONS = [
  { value: "before", label: "Before seat number" },
  { value: "after", label: "After seat number" }
]

export const NUMBER_INPUT_VALIDATIONS = [
  { value: "length", label: i18n.t("validation.fieldType.length") },
];

export const DATE_INPUT_VALIDATIONS = [
  { value: "dob", label: "DOB" },
]

export const BLOG_IS_HOT_POSITION = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export const SEAT_ROW_NAME_TYPES = [
  { value: "alphabet", label: "Alphabet" },
  { value: "number", label: "Number" }
]
export const SEAT_LAYOUT_TYPES = [
  { value: SeatLayoutType.ZONE, label: "Zone" },
  { value: SeatLayoutType.SEAT, label: "Seat" },
];

export const SEAT_STATUS = [
  { value: SeatLayoutStatus.AVAILABLE, label: "Available", readOnly: false },
  { value: SeatLayoutStatus.SOLD_OUT, label: "Sold out", readOnly: true },
  { value: SeatLayoutStatus.UNAVAILABLE, label: "Unavailable", readOnly: false },
  { value: SeatLayoutStatus.INACTIVE, label: "Inactive", readOnly: false },
  { value: SeatLayoutStatus.HIDE, label: "Hide", readOnly: false },
  { value: SeatLayoutStatus.WALKWAY, label: "Walkway", readOnly: false },
  { value: SeatLayoutStatus.WALKWAY_ON_THE_LEFT, label: "Walkway on the left", readOnly: false },
  { value: SeatLayoutStatus.WALKWAY_ON_THE_RIGHT, label: "Walkway on the right", readOnly: false },
  { value: SeatLayoutStatus.WALKWAY_ABOVE, label: "Walkway above", readOnly: false },
  { value: SeatLayoutStatus.WALKWAY_BELOW, label: "Walkway below", readOnly: false },
  { value: SeatLayoutStatus.IN_CART, label: "In cart", readOnly: true }
];

export const SEAT_POSITION = [
  { value: SeatLayoutPosition.LEFT_TOP_DOWN, label: "Left - Top-down"},
  { value: SeatLayoutPosition.LEFT_BOTTOM_UP, label: "Left - Bottom-up"},
  { value: SeatLayoutPosition.RIGHT_TOP_DOWN, label: "Right - Top-down"},
  { value: SeatLayoutPosition.RIGHT_BOTTOM_UP, label: "Right - Bottom-up"},
  { value: SeatLayoutPosition.TOP_LEFT, label: "Top-aligned left"},
  { value: SeatLayoutPosition.TOP_RIGHT, label: "Top-aligned right"},
  { value: SeatLayoutPosition.BOTTOM_LEFT, label: "Bottom-aligned left"},
  { value: SeatLayoutPosition.BOTTOM_RIGHT, label: "Bottom-aligned right"},
];

export const WALKWAY_VALUE = {
  IN_LEFT: "walkway_in_left",
  IN_RIGHT: "walkway_in_right",
}

export const FIELD_OBJECT_LIST = [
  { value: FieldObjectType.NATIONALITIES, label: i18n.t("select.fieldObjectType.nationality") },
  { value: FieldObjectType.COUNTRIES, label: i18n.t("select.fieldObjectType.country") },
  { value: FieldObjectType.CLUBS, label: i18n.t("select.fieldObjectType.club") },
  { value: FieldObjectType.GENDERS, label: i18n.t("select.fieldObjectType.gender") },
  { value: FieldObjectType.PROVINES, label: i18n.t("select.fieldObjectType.province") },
  { value: FieldObjectType.BLOOD_TYPE, label: i18n.t("select.fieldObjectType.bloodType") },
  { value: FieldObjectType.TSHIRT_SIZE, label: i18n.t("select.fieldObjectType.tShirtSize") },
  { value: FieldObjectType.TSHIRT_SIZE_FINISHER, label: i18n.t("select.fieldObjectType.finisherTShirtSize") },
  { value: FieldObjectType.RELATIONSHIP, label: i18n.t("select.fieldObjectType.relationship") },
  { value: FieldObjectType.CUSTOM, label: i18n.t("select.fieldObjectType.custom") }
]

export const OPTIONS_BY_OBJECT = {
  nationalities: NATIONALITIES_OBJECT,
  countries: COUNTRY_OBJECT,
  clubs: null,
  genders: GENDER_OBJECT,
  provinces: PROVINCE_OBJECT,
  blood_type: BLOOD_TYPE_OBJECT,
  tshirt_size: TSHIRT_SIZE_ARRAY,
  relationship: RELATIONSHIP_OBJECT,
  t_shirt_size_finisher: FINISHER_TSHIRT_SIZE_ARRAY,
  custom: [],
};

export const DEFAULT_TEMPLATE_GROUPS = [
  {
    key: "personal_information",
    value: {
      en: "Personal Information",
      vi: "Thông tin cá nhân",
    },
  },
];
export const DEFAULT_BANNER_URL =
  "https://pix.actiup.net/2023/11/09/empty_1440x600.png";

export const GENERATE_CODE_TYPES = [
  {label: "Manual Code", value: CouponCreateType.MANUAL },
  {label: "Generate Code", value: CouponCreateType.GENERATE },
];

export const APPLY_TO_PRODUCT_TYPES = [
  { label: "All Items", value: ApplyToProductType.ALL },
  { label: "Include Item(s)", value: ApplyToProductType.INCLUDE },
  { label: "Exclude Item(s)", value: ApplyToProductType.EXCLUDE },
];

export const DISCOUNT_TYPES = [
  { label: "Percentage ", value: DiscountType.PERCENT },
  { label: "Fixed Amount", value: DiscountType.FIX_PRICE },
  { label: "Quantity Discount", value: DiscountType.QUANTITY_DISCOUNT },
  { label: "Group Discount", value: DiscountType.GROUP_DISCOUNT },
  { label: "Campaign/Special Discount", value: DiscountType.CAMPAIGN_DISCOUNT },
];

export const APPLY_TO_LEVEL_OPTIONS = [
  { label: "Total Order ", value: ApplyToLevel.TOTAL_ORDER },
  { label: "Per Item", value: ApplyToLevel.PER_ITEM },
];

export const COUPON_CUSTOMER_TYPES = [
  { label: "Customer Code", value: CouponCustomerInfoType.CODE },
  { label: "Customer Email", value: CouponCustomerInfoType.EMAIL },
];

export const GENERATE_CHARACTER_POSITIONS = [
  { label: "Before The Code", value: GenerateCharacterPosition.PREFIX },
  { label: "After The Code", value: GenerateCharacterPosition.SUFFIX },
]

export const GATEWAY_OWNER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Actiup", value: "actiup" },
  { label: "Merchant", value: "merchant" },
];

export const COUNTRY_OPTIONS = [
  {
    "label": "Vietnam",
    "value": "Vietnam"
  },
  {
    "label": "Afghanistan",
    "value": "Afghanistan"
  },
  {
    "label": "Åland Islands",
    "value": "Åland Islands"
  },
  {
    "label": "Albania",
    "value": "Albania"
  },
  {
    "label": "Algeria",
    "value": "Algeria"
  },
  {
    "label": "American Samoa",
    "value": "American Samoa"
  },
  {
    "label": "Andorra",
    "value": "Andorra"
  },
  {
    "label": "Angola",
    "value": "Angola"
  },
  {
    "label": "Anguilla",
    "value": "Anguilla"
  },
  {
    "label": "Antarctica",
    "value": "Antarctica"
  },
  {
    "label": "Antigua and Barbuda",
    "value": "Antigua and Barbuda"
  },
  {
    "label": "Argentina",
    "value": "Argentina"
  },
  {
    "label": "Armenia",
    "value": "Armenia"
  },
  {
    "label": "Aruba",
    "value": "Aruba"
  },
  {
    "label": "Australia",
    "value": "Australia"
  },
  {
    "label": "Austria",
    "value": "Austria"
  },
  {
    "label": "Azerbaijan",
    "value": "Azerbaijan"
  },
  {
    "label": "Bahamas",
    "value": "Bahamas"
  },
  {
    "label": "Bahrain",
    "value": "Bahrain"
  },
  {
    "label": "Bangladesh",
    "value": "Bangladesh"
  },
  {
    "label": "Barbados",
    "value": "Barbados"
  },
  {
    "label": "Belarus",
    "value": "Belarus"
  },
  {
    "label": "Belgium",
    "value": "Belgium"
  },
  {
    "label": "Belize",
    "value": "Belize"
  },
  {
    "label": "Benin",
    "value": "Benin"
  },
  {
    "label": "Bermuda",
    "value": "Bermuda"
  },
  {
    "label": "Bhutan",
    "value": "Bhutan"
  },
  {
    "label": "Bolivia",
    "value": "Bolivia"
  },
  {
    "label": "Bosnia and Herzegovina",
    "value": "Bosnia and Herzegovina"
  },
  {
    "label": "Botswana",
    "value": "Botswana"
  },
  {
    "label": "Bouvet Island",
    "value": "Bouvet Island"
  },
  {
    "label": "Brazil",
    "value": "Brazil"
  },
  {
    "label": "British Indian Ocean Territory",
    "value": "British Indian Ocean Territory"
  },
  {
    "label": "Brunei Darussalam",
    "value": "Brunei Darussalam"
  },
  {
    "label": "Bulgaria",
    "value": "Bulgaria"
  },
  {
    "label": "Burkina Faso",
    "value": "Burkina Faso"
  },
  {
    "label": "Burundi",
    "value": "Burundi"
  },
  {
    "label": "Cambodia",
    "value": "Cambodia"
  },
  {
    "label": "Cameroon",
    "value": "Cameroon"
  },
  {
    "label": "Canada",
    "value": "Canada"
  },
  {
    "label": "Cape Verde",
    "value": "Cape Verde"
  },
  {
    "label": "Cayman Islands",
    "value": "Cayman Islands"
  },
  {
    "label": "Central African Republic",
    "value": "Central African Republic"
  },
  {
    "label": "Chad",
    "value": "Chad"
  },
  {
    "label": "Chile",
    "value": "Chile"
  },
  {
    "label": "China",
    "value": "China"
  },
  {
    "label": "Christmas Island",
    "value": "Christmas Island"
  },
  {
    "label": "Cocos (Keeling) Islands",
    "value": "Cocos (Keeling) Islands"
  },
  {
    "label": "Colombia",
    "value": "Colombia"
  },
  {
    "label": "Comoros",
    "value": "Comoros"
  },
  {
    "label": "Congo",
    "value": "Congo"
  },
  {
    "label": "Congo, The Democratic Republic of the",
    "value": "Congo, The Democratic Republic of the"
  },
  {
    "label": "Cook Islands",
    "value": "Cook Islands"
  },
  {
    "label": "Costa Rica",
    "value": "Costa Rica"
  },
  {
    "label": "Cote D'Ivoire",
    "value": "Cote D'Ivoire"
  },
  {
    "label": "Croatia",
    "value": "Croatia"
  },
  {
    "label": "Cuba",
    "value": "Cuba"
  },
  {
    "label": "Cyprus",
    "value": "Cyprus"
  },
  {
    "label": "Czech Republic",
    "value": "Czech Republic"
  },
  {
    "label": "Denmark",
    "value": "Denmark"
  },
  {
    "label": "Djibouti",
    "value": "Djibouti"
  },
  {
    "label": "Dominica",
    "value": "Dominica"
  },
  {
    "label": "Dominican Republic",
    "value": "Dominican Republic"
  },
  {
    "label": "Ecuador",
    "value": "Ecuador"
  },
  {
    "label": "Egypt",
    "value": "Egypt"
  },
  {
    "label": "El Salvador",
    "value": "El Salvador"
  },
  {
    "label": "Equatorial Guinea",
    "value": "Equatorial Guinea"
  },
  {
    "label": "Eritrea",
    "value": "Eritrea"
  },
  {
    "label": "Estonia",
    "value": "Estonia"
  },
  {
    "label": "Ethiopia",
    "value": "Ethiopia"
  },
  {
    "label": "Falkland Islands (Malvinas)",
    "value": "Falkland Islands (Malvinas)"
  },
  {
    "label": "Faroe Islands",
    "value": "Faroe Islands"
  },
  {
    "label": "Fiji",
    "value": "Fiji"
  },
  {
    "label": "Finland",
    "value": "Finland"
  },
  {
    "label": "France",
    "value": "France"
  },
  {
    "label": "French Guiana",
    "value": "French Guiana"
  },
  {
    "label": "French Polynesia",
    "value": "French Polynesia"
  },
  {
    "label": "French Southern Territories",
    "value": "French Southern Territories"
  },
  {
    "label": "Gabon",
    "value": "Gabon"
  },
  {
    "label": "Gambia",
    "value": "Gambia"
  },
  {
    "label": "Georgia",
    "value": "Georgia"
  },
  {
    "label": "Germany",
    "value": "Germany"
  },
  {
    "label": "Ghana",
    "value": "Ghana"
  },
  {
    "label": "Gibraltar",
    "value": "Gibraltar"
  },
  {
    "label": "Greece",
    "value": "Greece"
  },
  {
    "label": "Greenland",
    "value": "Greenland"
  },
  {
    "label": "Grenada",
    "value": "Grenada"
  },
  {
    "label": "Guadeloupe",
    "value": "Guadeloupe"
  },
  {
    "label": "Guam",
    "value": "Guam"
  },
  {
    "label": "Guatemala",
    "value": "Guatemala"
  },
  {
    "label": "Guernsey",
    "value": "Guernsey"
  },
  {
    "label": "Guinea",
    "value": "Guinea"
  },
  {
    "label": "Guinea-Bissau",
    "value": "Guinea-Bissau"
  },
  {
    "label": "Guyana",
    "value": "Guyana"
  },
  {
    "label": "Haiti",
    "value": "Haiti"
  },
  {
    "label": "Heard Island and Mcdonald Islands",
    "value": "Heard Island and Mcdonald Islands"
  },
  {
    "label": "Holy See (Vatican City State)",
    "value": "Holy See (Vatican City State)"
  },
  {
    "label": "Honduras",
    "value": "Honduras"
  },
  {
    "label": "Hong Kong",
    "value": "Hong Kong"
  },
  {
    "label": "Hungary",
    "value": "Hungary"
  },
  {
    "label": "Iceland",
    "value": "Iceland"
  },
  {
    "label": "India",
    "value": "India"
  },
  {
    "label": "Indonesia",
    "value": "Indonesia"
  },
  {
    "label": "Iran, Islamic Republic Of",
    "value": "Iran, Islamic Republic Of"
  },
  {
    "label": "Iraq",
    "value": "Iraq"
  },
  {
    "label": "Ireland",
    "value": "Ireland"
  },
  {
    "label": "Isle of Man",
    "value": "Isle of Man"
  },
  {
    "label": "Israel",
    "value": "Israel"
  },
  {
    "label": "Italy",
    "value": "Italy"
  },
  {
    "label": "Jamaica",
    "value": "Jamaica"
  },
  {
    "label": "Japan",
    "value": "Japan"
  },
  {
    "label": "Jersey",
    "value": "Jersey"
  },
  {
    "label": "Jordan",
    "value": "Jordan"
  },
  {
    "label": "Kazakhstan",
    "value": "Kazakhstan"
  },
  {
    "label": "Kenya",
    "value": "Kenya"
  },
  {
    "label": "Kiribati",
    "value": "Kiribati"
  },
  {
    "label": "Democratic People's Republic of Korea",
    "value": "Democratic People's Republic of Korea"
  },
  {
    "label": "Korea, Republic of",
    "value": "Korea, Republic of"
  },
  {
    "label": "Kosovo",
    "value": "Kosovo"
  },
  {
    "label": "Kuwait",
    "value": "Kuwait"
  },
  {
    "label": "Kyrgyzstan",
    "value": "Kyrgyzstan"
  },
  {
    "label": "Lao People's Democratic Republic",
    "value": "Lao People's Democratic Republic"
  },
  {
    "label": "Latvia",
    "value": "Latvia"
  },
  {
    "label": "Lebanon",
    "value": "Lebanon"
  },
  {
    "label": "Lesotho",
    "value": "Lesotho"
  },
  {
    "label": "Liberia",
    "value": "Liberia"
  },
  {
    "label": "Libyan Arab Jamahiriya",
    "value": "Libyan Arab Jamahiriya"
  },
  {
    "label": "Liechtenstein",
    "value": "Liechtenstein"
  },
  {
    "label": "Lithuania",
    "value": "Lithuania"
  },
  {
    "label": "Luxembourg",
    "value": "Luxembourg"
  },
  {
    "label": "Macao",
    "value": "Macao"
  },
  {
    "label": "Macedonia, The Former Yugoslav Republic of",
    "value": "Macedonia, The Former Yugoslav Republic of"
  },
  {
    "label": "Madagascar",
    "value": "Madagascar"
  },
  {
    "label": "Malawi",
    "value": "Malawi"
  },
  {
    "label": "Malaysia",
    "value": "Malaysia"
  },
  {
    "label": "Maldives",
    "value": "Maldives"
  },
  {
    "label": "Mali",
    "value": "Mali"
  },
  {
    "label": "Malta",
    "value": "Malta"
  },
  {
    "label": "Marshall Islands",
    "value": "Marshall Islands"
  },
  {
    "label": "Martinique",
    "value": "Martinique"
  },
  {
    "label": "Mauritania",
    "value": "Mauritania"
  },
  {
    "label": "Mauritius",
    "value": "Mauritius"
  },
  {
    "label": "Mayotte",
    "value": "Mayotte"
  },
  {
    "label": "Mexico",
    "value": "Mexico"
  },
  {
    "label": "Micronesia, Federated States of",
    "value": "Micronesia, Federated States of"
  },
  {
    "label": "Moldova, Republic of",
    "value": "Moldova, Republic of"
  },
  {
    "label": "Monaco",
    "value": "Monaco"
  },
  {
    "label": "Mongolia",
    "value": "Mongolia"
  },
  {
    "label": "Montenegro",
    "value": "Montenegro"
  },
  {
    "label": "Montserrat",
    "value": "Montserrat"
  },
  {
    "label": "Morocco",
    "value": "Morocco"
  },
  {
    "label": "Mozambique",
    "value": "Mozambique"
  },
  {
    "label": "Myanmar",
    "value": "Myanmar"
  },
  {
    "label": "Namibia",
    "value": "Namibia"
  },
  {
    "label": "Nauru",
    "value": "Nauru"
  },
  {
    "label": "Nepal",
    "value": "Nepal"
  },
  {
    "label": "Netherlands",
    "value": "Netherlands"
  },
  {
    "label": "Netherlands Antilles",
    "value": "Netherlands Antilles"
  },
  {
    "label": "New Caledonia",
    "value": "New Caledonia"
  },
  {
    "label": "New Zealand",
    "value": "New Zealand"
  },
  {
    "label": "Nicaragua",
    "value": "Nicaragua"
  },
  {
    "label": "Niger",
    "value": "Niger"
  },
  {
    "label": "Nigeria",
    "value": "Nigeria"
  },
  {
    "label": "Niue",
    "value": "Niue"
  },
  {
    "label": "Norfolk Island",
    "value": "Norfolk Island"
  },
  {
    "label": "Northern Mariana Islands",
    "value": "Northern Mariana Islands"
  },
  {
    "label": "Norway",
    "value": "Norway"
  },
  {
    "label": "Oman",
    "value": "Oman"
  },
  {
    "label": "Pakistan",
    "value": "Pakistan"
  },
  {
    "label": "Palau",
    "value": "Palau"
  },
  {
    "label": "Palestinian Territory, Occupied",
    "value": "Palestinian Territory, Occupied"
  },
  {
    "label": "Panama",
    "value": "Panama"
  },
  {
    "label": "Papua New Guinea",
    "value": "Papua New Guinea"
  },
  {
    "label": "Paraguay",
    "value": "Paraguay"
  },
  {
    "label": "Peru",
    "value": "Peru"
  },
  {
    "label": "Philippines",
    "value": "Philippines"
  },
  {
    "label": "Pitcairn",
    "value": "Pitcairn"
  },
  {
    "label": "Poland",
    "value": "Poland"
  },
  {
    "label": "Portugal",
    "value": "Portugal"
  },
  {
    "label": "Puerto Rico",
    "value": "Puerto Rico"
  },
  {
    "label": "Qatar",
    "value": "Qatar"
  },
  {
    "label": "Reunion",
    "value": "Reunion"
  },
  {
    "label": "Romania",
    "value": "Romania"
  },
  {
    "label": "Russian Federation",
    "value": "Russian Federation"
  },
  {
    "label": "Rwanda",
    "value": "Rwanda"
  },
  {
    "label": "Saint Helena",
    "value": "Saint Helena"
  },
  {
    "label": "Saint Kitts and Nevis",
    "value": "Saint Kitts and Nevis"
  },
  {
    "label": "Saint Lucia",
    "value": "Saint Lucia"
  },
  {
    "label": "Saint Pierre and Miquelon",
    "value": "Saint Pierre and Miquelon"
  },
  {
    "label": "Saint Vincent and the Grenadines",
    "value": "Saint Vincent and the Grenadines"
  },
  {
    "label": "Samoa",
    "value": "Samoa"
  },
  {
    "label": "San Marino",
    "value": "San Marino"
  },
  {
    "label": "Sao Tome and Principe",
    "value": "Sao Tome and Principe"
  },
  {
    "label": "Saudi Arabia",
    "value": "Saudi Arabia"
  },
  {
    "label": "Senegal",
    "value": "Senegal"
  },
  {
    "label": "Serbia",
    "value": "Serbia"
  },
  {
    "label": "Seychelles",
    "value": "Seychelles"
  },
  {
    "label": "Sierra Leone",
    "value": "Sierra Leone"
  },
  {
    "label": "Singapore",
    "value": "Singapore"
  },
  {
    "label": "Slovakia",
    "value": "Slovakia"
  },
  {
    "label": "Slovenia",
    "value": "Slovenia"
  },
  {
    "label": "Solomon Islands",
    "value": "Solomon Islands"
  },
  {
    "label": "Somalia",
    "value": "Somalia"
  },
  {
    "label": "South Africa",
    "value": "South Africa"
  },
  {
    "label": "South Georgia and the South Sandwich Islands",
    "value": "South Georgia and the South Sandwich Islands"
  },
  {
    "label": "Spain",
    "value": "Spain"
  },
  {
    "label": "Sri Lanka",
    "value": "Sri Lanka"
  },
  {
    "label": "Sudan",
    "value": "Sudan"
  },
  {
    "label": "Suriname",
    "value": "Suriname"
  },
  {
    "label": "Svalbard and Jan Mayen",
    "value": "Svalbard and Jan Mayen"
  },
  {
    "label": "Swaziland",
    "value": "Swaziland"
  },
  {
    "label": "Sweden",
    "value": "Sweden"
  },
  {
    "label": "Switzerland",
    "value": "Switzerland"
  },
  {
    "label": "Syrian Arab Republic",
    "value": "Syrian Arab Republic"
  },
  {
    "label": "Taiwan",
    "value": "Taiwan"
  },
  {
    "label": "Tajikistan",
    "value": "Tajikistan"
  },
  {
    "label": "Tanzania, United Republic of",
    "value": "Tanzania, United Republic of"
  },
  {
    "label": "Thailand",
    "value": "Thailand"
  },
  {
    "label": "Timor-Leste",
    "value": "Timor-Leste"
  },
  {
    "label": "Togo",
    "value": "Togo"
  },
  {
    "label": "Tokelau",
    "value": "Tokelau"
  },
  {
    "label": "Tonga",
    "value": "Tonga"
  },
  {
    "label": "Trinidad and Tobago",
    "value": "Trinidad and Tobago"
  },
  {
    "label": "Tunisia",
    "value": "Tunisia"
  },
  {
    "label": "Turkey",
    "value": "Turkey"
  },
  {
    "label": "Turkmenistan",
    "value": "Turkmenistan"
  },
  {
    "label": "Turks and Caicos Islands",
    "value": "Turks and Caicos Islands"
  },
  {
    "label": "Tuvalu",
    "value": "Tuvalu"
  },
  {
    "label": "Uganda",
    "value": "Uganda"
  },
  {
    "label": "Ukraine",
    "value": "Ukraine"
  },
  {
    "label": "United Arab Emirates",
    "value": "United Arab Emirates"
  },
  {
    "label": "United Kingdom",
    "value": "United Kingdom"
  },
  {
    "label": "United States",
    "value": "United States"
  },
  {
    "label": "United States Minor Outlying Islands",
    "value": "United States Minor Outlying Islands"
  },
  {
    "label": "Uruguay",
    "value": "Uruguay"
  },
  {
    "label": "Uzbekistan",
    "value": "Uzbekistan"
  },
  {
    "label": "Vanuatu",
    "value": "Vanuatu"
  },
  {
    "label": "Venezuela",
    "value": "Venezuela"
  },
  {
    "label": "Virgin Islands, British",
    "value": "Virgin Islands, British"
  },
  {
    "label": "Virgin Islands, U.S.",
    "value": "Virgin Islands, U.S."
  },
  {
    "label": "Wallis and Futuna",
    "value": "Wallis and Futuna"
  },
  {
    "label": "Western Sahara",
    "value": "Western Sahara"
  },
  {
    "label": "Yemen",
    "value": "Yemen"
  },
  {
    "label": "Zambia",
    "value": "Zambia"
  },
  {
    "label": "Zimbabwe",
    "value": "Zimbabwe"
  }
]

export const NATIONALITY_OPTIONS = [
  { label: "Vietnam", value: "VN" },
  { label: "Afghanistan", value: "AF" },
  { label: "land Islands", value: "AX" },
  { label: "Albania", value: "AL" },
  { label: "Algeria", value: "DZ" },
  { label: "American Samoa", value: "AS" },
  { label: "AndorrA", value: "AD" },
  { label: "Angola", value: "AO" },
  { label: "Anguilla", value: "AI" },
  { label: "Antarctica", value: "AQ" },
  { label: "Antigua and Barbuda", value: "AG" },
  { label: "Argentina", value: "AR" },
  { label: "Armenia", value: "AM" },
  { label: "Aruba", value: "AW" },
  { label: "Australia", value: "AU" },
  { label: "Austria", value: "AT" },
  { label: "Azerbaijan", value: "AZ" },
  { label: "Bahamas", value: "BS" },
  { label: "Bahrain", value: "BH" },
  { label: "Bangladesh", value: "BD" },
  { label: "Barbados", value: "BB" },
  { label: "Belarus", value: "BY" },
  { label: "Belgium", value: "BE" },
  { label: "Belize", value: "BZ" },
  { label: "Benin", value: "BJ" },
  { label: "Bermuda", value: "BM" },
  { label: "Bhutan", value: "BT" },
  { label: "Bolivia", value: "BO" },
  { label: "Bosnia and Herzegovina", value: "BA" },
  { label: "Botswana", value: "BW" },
  { label: "Bouvet Island", value: "BV" },
  { label: "Brazil", value: "BR" },
  { label: "British Indian Ocean Territory", value: "IO" },
  { label: "Brunei Darussalam", value: "BN" },
  { label: "Bulgaria", value: "BG" },
  { label: "Burkina Faso", value: "BF" },
  { label: "Burundi", value: "BI" },
  { label: "Cambodia", value: "KH" },
  { label: "Cameroon", value: "CM" },
  { label: "Canada", value: "CA" },
  { label: "Cape Verde", value: "CV" },
  { label: "Cayman Islands", value: "KY" },
  { label: "Central African Republic", value: "CF" },
  { label: "Chad", value: "TD" },
  { label: "Chile", value: "CL" },
  { label: "China", value: "CN" },
  { label: "Christmas Island", value: "CX" },
  { label: "Cocos (Keeling) Islands", value: "CC" },
  { label: "Colombia", value: "CO" },
  { label: "Comoros", value: "KM" },
  { label: "Congo", value: "CG" },
  { label: "Congo, The Democratic Republic of the", value: "CD" },
  { label: "Cook Islands", value: "CK" },
  { label: "Costa Rica", value: "CR" },
  { label: "Cote D'Ivoire", value: "CI" },
  { label: "Croatia", value: "HR" },
  { label: "Cuba", value: "CU" },
  { label: "Cyprus", value: "CY" },
  { label: "Czech Republic", value: "CZ" },
  { label: "Denmark", value: "DK" },
  { label: "Djibouti", value: "DJ" },
  { label: "Dominica", value: "DM" },
  { label: "Dominican Republic", value: "DO" },
  { label: "Ecuador", value: "EC" },
  { label: "Egypt", value: "EG" },
  { label: "El Salvador", value: "SV" },
  { label: "Equatorial Guinea", value: "GQ" },
  { label: "Eritrea", value: "ER" },
  { label: "Estonia", value: "EE" },
  { label: "Ethiopia", value: "ET" },
  { label: "Falkland Islands (Malvinas)", value: "FK" },
  { label: "Faroe Islands", value: "FO" },
  { label: "Fiji", value: "FJ" },
  { label: "Finland", value: "FI" },
  { label: "France", value: "FR" },
  { label: "French Guiana", value: "GF" },
  { label: "French Polynesia", value: "PF" },
  { label: "French Southern Territories", value: "TF" },
  { label: "Gabon", value: "GA" },
  { label: "Gambia", value: "GM" },
  { label: "Georgia", value: "GE" },
  { label: "Germany", value: "DE" },
  { label: "Ghana", value: "GH" },
  { label: "Gibraltar", value: "GI" },
  { label: "Greece", value: "GR" },
  { label: "Greenland", value: "GL" },
  { label: "Grenada", value: "GD" },
  { label: "Guadeloupe", value: "GP" },
  { label: "Guam", value: "GU" },
  { label: "Guatemala", value: "GT" },
  { label: "Guernsey", value: "GG" },
  { label: "Guinea", value: "GN" },
  { label: "Guinea-Bissau", value: "GW" },
  { label: "Guyana", value: "GY" },
  { label: "Haiti", value: "HT" },
  { label: "Heard Island and Mcdonald Islands", value: "HM" },
  { label: "Holy See (Vatican City State)", value: "VA" },
  { label: "Honduras", value: "HN" },
  { label: "Hong Kong", value: "HK" },
  { label: "Hungary", value: "HU" },
  { label: "Iceland", value: "IS" },
  { label: "India", value: "IN" },
  { label: "Indonesia", value: "ID" },
  { label: "Iran, Islamic Republic Of", value: "IR" },
  { label: "Iraq", value: "IQ" },
  { label: "Ireland", value: "IE" },
  { label: "Isle of Man", value: "IM" },
  { label: "Israel", value: "IL" },
  { label: "Italy", value: "IT" },
  { label: "Jamaica", value: "JM" },
  { label: "Japan", value: "JP" },
  { label: "Jersey", value: "JE" },
  { label: "Jordan", value: "JO" },
  { label: "Kazakhstan", value: "KZ" },
  { label: "Kenya", value: "KE" },
  { label: "Kiribati", value: "KI" },
  { label: "Korea, Democratic People'S Republic of", value: "KP" },
  { label: "Korea, Republic of", value: "KR" },
  { label: "Kuwait", value: "KW" },
  { label: "Kyrgyzstan", value: "KG" },
  { label: "Lao People'S Democratic Republic", value: "LA" },
  { label: "Latvia", value: "LV" },
  { label: "Lebanon", value: "LB" },
  { label: "Lesotho", value: "LS" },
  { label: "Liberia", value: "LR" },
  { label: "Libyan Arab Jamahiriya", value: "LY" },
  { label: "Liechtenstein", value: "LI" },
  { label: "Lithuania", value: "LT" },
  { label: "Luxembourg", value: "LU" },
  { label: "Macao", value: "MO" },
  { label: "Macedonia, The Former Yugoslav Republic of", value: "MK" },
  { label: "Madagascar", value: "MG" },
  { label: "Malawi", value: "MW" },
  { label: "Malaysia", value: "MY" },
  { label: "Maldives", value: "MV" },
  { label: "Mali", value: "ML" },
  { label: "Malta", value: "MT" },
  { label: "Marshall Islands", value: "MH" },
  { label: "Martinique", value: "MQ" },
  { label: "Mauritania", value: "MR" },
  { label: "Mauritius", value: "MU" },
  { label: "Mayotte", value: "YT" },
  { label: "Mexico", value: "MX" },
  { label: "Micronesia, Federated States of", value: "FM" },
  { label: "Moldova, Republic of", value: "MD" },
  { label: "Monaco", value: "MC" },
  { label: "Mongolia", value: "MN" },
  { label: "Montenegro", value: "ME" },
  { label: "Montserrat", value: "MS" },
  { label: "Morocco", value: "MA" },
  { label: "Mozambique", value: "MZ" },
  { label: "Myanmar", value: "MM" },
  { label: "Namibia", value: "NA" },
  { label: "Nauru", value: "NR" },
  { label: "Nepal", value: "NP" },
  { label: "Netherlands", value: "NL" },
  { label: "Netherlands Antilles", value: "AN" },
  { label: "New Caledonia", value: "NC" },
  { label: "New Zealand", value: "NZ" },
  { label: "Nicaragua", value: "NI" },
  { label: "Niger", value: "NE" },
  { label: "Nigeria", value: "NG" },
  { label: "Niue", value: "NU" },
  { label: "Norfolk Island", value: "NF" },
  { label: "Northern Mariana Islands", value: "MP" },
  { label: "Norway", value: "NO" },
  { label: "Oman", value: "OM" },
  { label: "Pakistan", value: "PK" },
  { label: "Palau", value: "PW" },
  { label: "Palestinian Territory, Occupied", value: "PS" },
  { label: "Panama", value: "PA" },
  { label: "Papua New Guinea", value: "PG" },
  { label: "Paraguay", value: "PY" },
  { label: "Peru", value: "PE" },
  { label: "Philippines", value: "PH" },
  { label: "Pitcairn", value: "PN" },
  { label: "Poland", value: "PL" },
  { label: "Portugal", value: "PT" },
  { label: "Puerto Rico", value: "PR" },
  { label: "Qatar", value: "QA" },
  { label: "Reunion", value: "RE" },
  { label: "Romania", value: "RO" },
  { label: "Russian Federation", value: "RU" },
  { label: "RWANDA", value: "RW" },
  { label: "Saint Helena", value: "SH" },
  { label: "Saint Kitts and Nevis", value: "KN" },
  { label: "Saint Lucia", value: "LC" },
  { label: "Saint Pierre and Miquelon", value: "PM" },
  { label: "Saint Vincent and the Grenadines", value: "VC" },
  { label: "Samoa", value: "WS" },
  { label: "San Marino", value: "SM" },
  { label: "Sao Tome and Principe", value: "ST" },
  { label: "Saudi Arabia", value: "SA" },
  { label: "Senegal", value: "SN" },
  { label: "Serbia", value: "RS" },
  { label: "Seychelles", value: "SC" },
  { label: "Sierra Leone", value: "SL" },
  { label: "Singapore", value: "SG" },
  { label: "Slovakia", value: "SK" },
  { label: "Slovenia", value: "SI" },
  { label: "Solomon Islands", value: "SB" },
  { label: "Somalia", value: "SO" },
  { label: "South Africa", value: "ZA" },
  { label: "South Georgia and the South Sandwich Islands", value: "GS" },
  { label: "Spain", value: "ES" },
  { label: "Sri Lanka", value: "LK" },
  { label: "Sudan", value: "SD" },
  { label: "Surilabel", value: "SR" },
  { label: "Svalbard and Jan Mayen", value: "SJ" },
  { label: "Swaziland", value: "SZ" },
  { label: "Sweden", value: "SE" },
  { label: "Switzerland", value: "CH" },
  { label: "Syrian Arab Republic", value: "SY" },
  { label: "Taiwan, Province of China", value: "TW" },
  { label: "Tajikistan", value: "TJ" },
  { label: "Tanzania, United Republic of", value: "TZ" },
  { label: "Thailand", value: "TH" },
  { label: "Timor-Leste", value: "TL" },
  { label: "Togo", value: "TG" },
  { label: "Tokelau", value: "TK" },
  { label: "Tonga", value: "TO" },
  { label: "Trinidad and Tobago", value: "TT" },
  { label: "Tunisia", value: "TN" },
  { label: "Turkey", value: "TR" },
  { label: "Turkmenistan", value: "TM" },
  { label: "Turks and Caicos Islands", value: "TC" },
  { label: "Tuvalu", value: "TV" },
  { label: "Uganda", value: "UG" },
  { label: "Ukraine", value: "UA" },
  { label: "United Arab Emirates", value: "AE" },
  { label: "United Kingdom", value: "GB" },
  { label: "United States", value: "US" },
  { label: "United States Minor Outlying Islands", value: "UM" },
  { label: "Uruguay", value: "UY" },
  { label: "Uzbekistan", value: "UZ" },
  { label: "Vanuatu", value: "VU" },
  { label: "Venezuela", value: "VE" },
  { label: "Virgin Islands, British", value: "VG" },
  { label: "Virgin Islands, U.S.", value: "VI" },
  { label: "Wallis and Futuna", value: "WF" },
  { label: "Western Sahara", value: "EH" },
  { label: "Yemen", value: "YE" },
  { label: "Zambia", value: "ZM" },
  { label: "Zimbabwe", value: "ZW" },
];

export const PHONE_CODE = [
  {
    label: "Afghanistan +93",
    value: "AF",
    code: "93",
  },
  {
    label: "Aland Islands +358",
    value: "AX",
    code: "358",
  },
  {
    label: "Albania +355",
    value: "AL",
    code: "355",
  },
  {
    label: "Algeria +213",
    value: "DZ",
    code: "213",
  },
  {
    label: "AmericanSamoa +1684",
    value: "AS",
    code: "1684",
  },
  {
    label: "Andorra +376",
    value: "AD",
    code: "376",
  },
  {
    label: "Angola +244",
    value: "AO",
    code: "244",
  },
  {
    label: "Anguilla +1264",
    value: "AI",
    code: "1264",
  },
  {
    label: "Antarctica +672",
    value: "AQ",
    code: "672",
  },
  {
    label: "Antigua and Barbuda +1268",
    value: "AG",
    code: "1268",
  },
  {
    label: "Argentina +54",
    value: "AR",
    code: "54",
  },
  {
    label: "Armenia +374",
    value: "AM",
    code: "374",
  },
  {
    label: "Aruba +297",
    value: "AW",
    code: "297",
  },
  {
    label: "Australia +61",
    value: "AU",
    code: "61",
  },
  {
    label: "Austria +43",
    value: "AT",
    code: "43",
  },
  {
    label: "Azerbaijan +994",
    value: "AZ",
    code: "994",
  },
  {
    label: "Bahamas +1242",
    value: "BS",
    code: "1242",
  },
  {
    label: "Bahrain +973",
    value: "BH",
    code: "973",
  },
  {
    label: "Bangladesh +880",
    value: "BD",
    code: "880",
  },
  {
    label: "Barbados +1246",
    value: "BB",
    code: "1246",
  },
  {
    label: "Belarus +375",
    value: "BY",
    code: "375",
  },
  {
    label: "Belgium +32",
    value: "BE",
    code: "32",
  },
  {
    label: "Belize +501",
    value: "BZ",
    code: "501",
  },
  {
    label: "Benin +229",
    value: "BJ",
    code: "229",
  },
  {
    label: "Bermuda +1441",
    value: "BM",
    code: "1441",
  },
  {
    label: "Bhutan +975",
    value: "BT",
    code: "975",
  },
  {
    label: "Bolivia, Plurinational State of +591",
    value: "BO",
    code: "591",
  },
  {
    label: "Bosnia and Herzegovina +387",
    value: "BA",
    code: "387",
  },
  {
    label: "Botswana +267",
    value: "BW",
    code: "267",
  },
  {
    label: "Brazil +55",
    value: "BR",
    code: "55",
  },
  {
    label: "British Indian Ocean Territory +246",
    value: "IO",
    code: "246",
  },
  {
    label: "Brunei Darussalam +673",
    value: "BN",
    code: "673",
  },
  {
    label: "Bulgaria +359",
    value: "BG",
    code: "359",
  },
  {
    label: "Burkina Faso +226",
    value: "BF",
    code: "226",
  },
  {
    label: "Burundi +257",
    value: "BI",
    code: "257",
  },
  {
    label: "Cambodia +855",
    value: "KH",
    code: "855",
  },
  {
    label: "Cameroon +237",
    value: "CM",
    code: "237",
  },
  {
    label: "Canada +1",
    value: "CA",
    code: "1",
  },
  {
    label: "Cape Verde +238",
    value: "CV",
    code: "238",
  },
  {
    label: "Cayman Islands + 345",
    value: "KY",
    code: " 345",
  },
  {
    label: "Central African Republic +236",
    value: "CF",
    code: "236",
  },
  {
    label: "Chad +235",
    value: "TD",
    code: "235",
  },
  {
    label: "Chile +56",
    value: "CL",
    code: "56",
  },
  {
    label: "China +86",
    value: "CN",
    code: "86",
  },
  {
    label: "Christmas Island +61",
    value: "CX",
    code: "61",
  },
  {
    label: "Cocos (Keeling) Islands +61",
    value: "CC",
    code: "61",
  },
  {
    label: "Colombia +57",
    value: "CO",
    code: "57",
  },
  {
    label: "Comoros +269",
    value: "KM",
    code: "269",
  },
  {
    label: "Congo +242",
    value: "CG",
    code: "242",
  },
  {
    label: "Congo, The Democratic Republic of the Congo +243",
    value: "CD",
    code: "243",
  },
  {
    label: "Cook Islands +682",
    value: "CK",
    code: "682",
  },
  {
    label: "Costa Rica +506",
    value: "CR",
    code: "506",
  },
  {
    label: "Cote d'Ivoire +225",
    value: "CI",
    code: "225",
  },
  {
    label: "Croatia +385",
    value: "HR",
    code: "385",
  },
  {
    label: "Cuba +53",
    value: "CU",
    code: "53",
  },
  {
    label: "Cyprus +357",
    value: "CY",
    code: "357",
  },
  {
    label: "Czech Republic +420",
    value: "CZ",
    code: "420",
  },
  {
    label: "Denmark +45",
    value: "DK",
    code: "45",
  },
  {
    label: "Djibouti +253",
    value: "DJ",
    code: "253",
  },
  {
    label: "Dominica +1767",
    value: "DM",
    code: "1767",
  },
  {
    label: "Dominican Republic +1849",
    value: "DO",
    code: "1849",
  },
  {
    label: "Ecuador +593",
    value: "EC",
    code: "593",
  },
  {
    label: "Egypt +20",
    value: "EG",
    code: "20",
  },
  {
    label: "El Salvador +503",
    value: "SV",
    code: "503",
  },
  {
    label: "Equatorial Guinea +240",
    value: "GQ",
    code: "240",
  },
  {
    label: "Eritrea +291",
    value: "ER",
    code: "291",
  },
  {
    label: "Estonia +372",
    value: "EE",
    code: "372",
  },
  {
    label: "Ethiopia +251",
    value: "ET",
    code: "251",
  },
  {
    label: "Falkland Islands (Malvinas) +500",
    value: "FK",
    code: "500",
  },
  {
    label: "Faroe Islands +298",
    value: "FO",
    code: "298",
  },
  {
    label: "Fiji +679",
    value: "FJ",
    code: "679",
  },
  {
    label: "Finland +358",
    value: "FI",
    code: "358",
  },
  {
    label: "France +33",
    value: "FR",
    code: "33",
  },
  {
    label: "French Guiana +594",
    value: "GF",
    code: "594",
  },
  {
    label: "French Polynesia +689",
    value: "PF",
    code: "689",
  },
  {
    label: "Gabon +241",
    value: "GA",
    code: "241",
  },
  {
    label: "Gambia +220",
    value: "GM",
    code: "220",
  },
  {
    label: "Georgia +995",
    value: "GE",
    code: "995",
  },
  {
    label: "Germany +49",
    value: "DE",
    code: "49",
  },
  {
    label: "Ghana +233",
    value: "GH",
    code: "233",
  },
  {
    label: "Gibraltar +350",
    value: "GI",
    code: "350",
  },
  {
    label: "Greece +30",
    value: "GR",
    code: "30",
  },
  {
    label: "Greenland +299",
    value: "GL",
    code: "299",
  },
  {
    label: "Grenada +1473",
    value: "GD",
    code: "1473",
  },
  {
    label: "Guadeloupe +590",
    value: "GP",
    code: "590",
  },
  {
    label: "Guam +1671",
    value: "GU",
    code: "1671",
  },
  {
    label: "Guatemala +502",
    value: "GT",
    code: "502",
  },
  {
    label: "Guernsey +44",
    value: "GG",
    code: "44",
  },
  {
    label: "Guinea +224",
    value: "GN",
    code: "224",
  },
  {
    label: "Guinea-Bissau +245",
    value: "GW",
    code: "245",
  },
  {
    label: "Guyana +595",
    value: "GY",
    code: "595",
  },
  {
    label: "Haiti +509",
    value: "HT",
    code: "509",
  },
  {
    label: "Holy See (Vatican City State) +379",
    value: "VA",
    code: "379",
  },
  {
    label: "Honduras +504",
    value: "HN",
    code: "504",
  },
  {
    label: "Hong Kong +852",
    value: "HK",
    code: "852",
  },
  {
    label: "Hungary +36",
    value: "HU",
    code: "36",
  },
  {
    label: "Iceland +354",
    value: "IS",
    code: "354",
  },
  {
    label: "India +91",
    value: "IN",
    code: "91",
  },
  {
    label: "Indonesia +62",
    value: "ID",
    code: "62",
  },
  {
    label: "Iran, Islamic Republic of Persian Gulf +98",
    value: "IR",
    code: "98",
  },
  {
    label: "Iraq +964",
    value: "IQ",
    code: "964",
  },
  {
    label: "Ireland +353",
    value: "IE",
    code: "353",
  },
  {
    label: "Isle of Man +44",
    value: "IM",
    code: "44",
  },
  {
    label: "Israel +972",
    value: "IL",
    code: "972",
  },
  {
    label: "Italy +39",
    value: "IT",
    code: "39",
  },
  {
    label: "Jamaica +1876",
    value: "JM",
    code: "1876",
  },
  {
    label: "Japan +81",
    value: "JP",
    code: "81",
  },
  {
    label: "Jersey +44",
    value: "JE",
    code: "44",
  },
  {
    label: "Jordan +962",
    value: "JO",
    code: "962",
  },
  {
    label: "Kazakhstan +77",
    value: "KZ",
    code: "77",
  },
  {
    label: "Kenya +254",
    value: "KE",
    code: "254",
  },
  {
    label: "Kiribati +686",
    value: "KI",
    code: "686",
  },
  {
    label: "Korea, Democratic People's Republic of Korea +850",
    value: "KP",
    code: "850",
  },
  {
    label: "Korea, Republic of South Korea +82",
    value: "KR",
    code: "82",
  },
  {
    label: "Kuwait +965",
    value: "KW",
    code: "965",
  },
  {
    label: "Kyrgyzstan +996",
    value: "KG",
    code: "996",
  },
  {
    label: "Laos +856",
    value: "LA",
    code: "856",
  },
  {
    label: "Latvia +371",
    value: "LV",
    code: "371",
  },
  {
    label: "Lebanon +961",
    value: "LB",
    code: "961",
  },
  {
    label: "Lesotho +266",
    value: "LS",
    code: "266",
  },
  {
    label: "Liberia +231",
    value: "LR",
    code: "231",
  },
  {
    label: "Libyan Arab Jamahiriya +218",
    value: "LY",
    code: "218",
  },
  {
    label: "Liechtenstein +423",
    value: "LI",
    code: "423",
  },
  {
    label: "Lithuania +370",
    value: "LT",
    code: "370",
  },
  {
    label: "Luxembourg +352",
    value: "LU",
    code: "352",
  },
  {
    label: "Macao +853",
    value: "MO",
    code: "853",
  },
  {
    label: "Macedonia +389",
    value: "MK",
    code: "389",
  },
  {
    label: "Madagascar +261",
    value: "MG",
    code: "261",
  },
  {
    label: "Malawi +265",
    value: "MW",
    code: "265",
  },
  {
    label: "Malaysia +60",
    value: "MY",
    code: "60",
  },
  {
    label: "Maldives +960",
    value: "MV",
    code: "960",
  },
  {
    label: "Mali +223",
    value: "ML",
    code: "223",
  },
  {
    label: "Malta +356",
    value: "MT",
    code: "356",
  },
  {
    label: "Marshall Islands +692",
    value: "MH",
    code: "692",
  },
  {
    label: "Martinique +596",
    value: "MQ",
    code: "596",
  },
  {
    label: "Mauritania +222",
    value: "MR",
    code: "222",
  },
  {
    label: "Mauritius +230",
    value: "MU",
    code: "230",
  },
  {
    label: "Mayotte +262",
    value: "YT",
    code: "262",
  },
  {
    label: "Mexico +52",
    value: "MX",
    code: "52",
  },
  {
    label: "Micronesia, Federated States of Micronesia +691",
    value: "FM",
    code: "691",
  },
  {
    label: "Moldova +373",
    value: "MD",
    code: "373",
  },
  {
    label: "Monaco +377",
    value: "MC",
    code: "377",
  },
  {
    label: "Mongolia +976",
    value: "MN",
    code: "976",
  },
  {
    label: "Montenegro +382",
    value: "ME",
    code: "382",
  },
  {
    label: "Montserrat +1664",
    value: "MS",
    code: "1664",
  },
  {
    label: "Morocco +212",
    value: "MA",
    code: "212",
  },
  {
    label: "Mozambique +258",
    value: "MZ",
    code: "258",
  },
  {
    label: "Myanmar +95",
    value: "MM",
    code: "95",
  },
  {
    label: "Namibia +264",
    value: "NA",
    code: "264",
  },
  {
    label: "Nauru +674",
    value: "NR",
    code: "674",
  },
  {
    label: "Nepal +977",
    value: "NP",
    code: "977",
  },
  {
    label: "Netherlands +31",
    value: "NL",
    code: "31",
  },
  {
    label: "Netherlands Antilles +599",
    value: "AN",
    code: "599",
  },
  {
    label: "New Caledonia +687",
    value: "NC",
    code: "687",
  },
  {
    label: "New Zealand +64",
    value: "NZ",
    code: "64",
  },
  {
    label: "Nicaragua +505",
    value: "NI",
    code: "505",
  },
  {
    label: "Niger +227",
    value: "NE",
    code: "227",
  },
  {
    label: "Nigeria +234",
    value: "NG",
    code: "234",
  },
  {
    label: "Niue +683",
    value: "NU",
    code: "683",
  },
  {
    label: "Norfolk Island +672",
    value: "NF",
    code: "672",
  },
  {
    label: "Northern Mariana Islands +1670",
    value: "MP",
    code: "1670",
  },
  {
    label: "Norway +47",
    value: "NO",
    code: "47",
  },
  {
    label: "Oman +968",
    value: "OM",
    code: "968",
  },
  {
    label: "Pakistan +92",
    value: "PK",
    code: "92",
  },
  {
    label: "Palau +680",
    value: "PW",
    code: "680",
  },
  {
    label: "Palestinian Territory, Occupied +970",
    value: "PS",
    code: "970",
  },
  {
    label: "Panama +507",
    value: "PA",
    code: "507",
  },
  {
    label: "Papua New Guinea +675",
    value: "PG",
    code: "675",
  },
  {
    label: "Paraguay +595",
    value: "PY",
    code: "595",
  },
  {
    label: "Peru +51",
    value: "PE",
    code: "51",
  },
  {
    label: "Philippines +63",
    value: "PH",
    code: "63",
  },
  {
    label: "Pitcairn +872",
    value: "PN",
    code: "872",
  },
  {
    label: "Poland +48",
    value: "PL",
    code: "48",
  },
  {
    label: "Portugal +351",
    value: "PT",
    code: "351",
  },
  {
    label: "Puerto Rico +1939",
    value: "PR",
    code: "1939",
  },
  {
    label: "Qatar +974",
    value: "QA",
    code: "974",
  },
  {
    label: "Romania +40",
    value: "RO",
    code: "40",
  },
  {
    label: "Russia +7",
    value: "RU",
    code: "7",
  },
  {
    label: "Rwanda +250",
    value: "RW",
    code: "250",
  },
  {
    label: "Reunion +262",
    value: "RE",
    code: "262",
  },
  {
    label: "Saint Barthelemy +590",
    value: "BL",
    code: "590",
  },
  {
    label: "Saint Helena, Ascension and Tristan Da Cunha +290",
    value: "SH",
    code: "290",
  },
  {
    label: "Saint Kitts and Nevis +1869",
    value: "KN",
    code: "1869",
  },
  {
    label: "Saint Lucia +1758",
    value: "LC",
    code: "1758",
  },
  {
    label: "Saint Martin +590",
    value: "MF",
    code: "590",
  },
  {
    label: "Saint Pierre and Miquelon +508",
    value: "PM",
    code: "508",
  },
  {
    label: "Saint Vincent and the Grenadines +1784",
    value: "VC",
    code: "1784",
  },
  {
    label: "Samoa +685",
    value: "WS",
    code: "685",
  },
  {
    label: "San Marino +378",
    value: "SM",
    code: "378",
  },
  {
    label: "Sao Tome and Principe +239",
    value: "ST",
    code: "239",
  },
  {
    label: "Saudi Arabia +966",
    value: "SA",
    code: "966",
  },
  {
    label: "Senegal +221",
    value: "SN",
    code: "221",
  },
  {
    label: "Serbia +381",
    value: "RS",
    code: "381",
  },
  {
    label: "Seychelles +248",
    value: "SC",
    code: "248",
  },
  {
    label: "Sierra Leone +232",
    value: "SL",
    code: "232",
  },
  {
    label: "Singapore +65",
    value: "SG",
    code: "65",
  },
  {
    label: "Slovakia +421",
    value: "SK",
    code: "421",
  },
  {
    label: "Slovenia +386",
    value: "SI",
    code: "386",
  },
  {
    label: "Solomon Islands +677",
    value: "SB",
    code: "677",
  },
  {
    label: "Somalia +252",
    value: "SO",
    code: "252",
  },
  {
    label: "South Africa +27",
    value: "ZA",
    code: "27",
  },
  {
    label: "South Sudan +211",
    value: "SS",
    code: "211",
  },
  {
    label: "South Georgia and the South Sandwich Islands +500",
    value: "GS",
    code: "500",
  },
  {
    label: "Spain +34",
    value: "ES",
    code: "34",
  },
  {
    label: "Sri Lanka +94",
    value: "LK",
    code: "94",
  },
  {
    label: "Sudan +249",
    value: "SD",
    code: "249",
  },
  {
    label: "Suriname +597",
    value: "SR",
    code: "597",
  },
  {
    label: "Svalbard and Jan Mayen +47",
    value: "SJ",
    code: "47",
  },
  {
    label: "Swaziland +268",
    value: "SZ",
    code: "268",
  },
  {
    label: "Sweden +46",
    value: "SE",
    code: "46",
  },
  {
    label: "Switzerland +41",
    value: "CH",
    code: "41",
  },
  {
    label: "Syrian Arab Republic +963",
    value: "SY",
    code: "963",
  },
  {
    label: "Taiwan +886",
    value: "TW",
    code: "886",
  },
  {
    label: "Tajikistan +992",
    value: "TJ",
    code: "992",
  },
  {
    label: "Tanzania, United Republic of Tanzania +255",
    value: "TZ",
    code: "255",
  },
  {
    label: "Thailand +66",
    value: "TH",
    code: "66",
  },
  {
    label: "Timor-Leste +670",
    value: "TL",
    code: "670",
  },
  {
    label: "Togo +228",
    value: "TG",
    code: "228",
  },
  {
    label: "Tokelau +690",
    value: "TK",
    code: "690",
  },
  {
    label: "Tonga +676",
    value: "TO",
    code: "676",
  },
  {
    label: "Trinidad and Tobago +1868",
    value: "TT",
    code: "1868",
  },
  {
    label: "Tunisia +216",
    value: "TN",
    code: "216",
  },
  {
    label: "Turkey +90",
    value: "TR",
    code: "90",
  },
  {
    label: "Turkmenistan +993",
    value: "TM",
    code: "993",
  },
  {
    label: "Turks and Caicos Islands +1649",
    value: "TC",
    code: "1649",
  },
  {
    label: "Tuvalu +688",
    value: "TV",
    code: "688",
  },
  {
    label: "Uganda +256",
    value: "UG",
    code: "256",
  },
  {
    label: "Ukraine +380",
    value: "UA",
    code: "380",
  },
  {
    label: "United Arab Emirates +971",
    value: "AE",
    code: "971",
  },
  {
    label: "United Kingdom +44",
    value: "GB",
    code: "44",
  },
  {
    label: "United States +1",
    value: "US",
    code: "1",
  },
  {
    label: "Uruguay +598",
    value: "UY",
    code: "598",
  },
  {
    label: "Uzbekistan +998",
    value: "UZ",
    code: "998",
  },
  {
    label: "Vanuatu +678",
    value: "VU",
    code: "678",
  },
  {
    label: "Venezuela, Bolivarian Republic of Venezuela +58",
    value: "VE",
    code: "58",
  },
  {
    label: "Vietnam +84",
    value: "VN",
    code: "84",
  },
  {
    label: "Virgin Islands, British +1284",
    value: "VG",
    code: "1284",
  },
  {
    label: "Virgin Islands, U.S. +1340",
    value: "VI",
    code: "1340",
  },
  {
    label: "Wallis and Futuna +681",
    value: "WF",
    code: "681",
  },
  {
    label: "Yemen +967",
    value: "YE",
    code: "967",
  },
  {
    label: "Zambia +260",
    value: "ZM",
    code: "260",
  },
  {
    label: "Zimbabwe +263",
    value: "ZW",
    code: "263",
  },
];

export const PROVINCE = [
  {
    name_vi: "Thành phố Hà Nội",
    name_en: "Ha Noi City",
    value: "ha_noi",
  },
  {
    name_vi: "Thành phố Hồ Chí Minh",
    name_en: "Ho Chi Minh City",
    value: "ho_chi_minh",
  },
  {
    name_vi: "Thành phố Hải Phòng",
    name_en: "Hai Phong City",
    value: "hai_phong",
  },
  {
    name_vi: "Thành phố Đà Nẵng",
    name_en: "Da Nang City",
    value: "da_nang",
  },
  {
    name_vi: "Thành phố Cần Thơ",
    name_en: "Can Tho City",
    value: "can_tho",
  },
  {
    name_vi: "Thành phố Huế",
    name_en: "Hue City",
    value: "hue",
  },
  {
    name_vi: "An Giang",
    name_en: "An Giang",
    value: "an_giang",
  },
  {
    name_vi: "Bắc Ninh",
    name_en: "Bac Ninh",
    value: "bac_ninh",
  },
  {
    name_vi: "Cà Mau",
    name_en: "Ca Mau",
    value: "ca_mau",
  },
  {
    name_vi: "Cao Bằng",
    name_en: "Cao Bang",
    value: "cao_bang",
  },
  {
    name_vi: "Đắk Lắk",
    name_en: "Dak Lak",
    value: "dak_lak",
  },
  {
    name_vi: "Điện Biên",
    name_en: "Dien Bien",
    value: "dien_bien",
  },
  {
    name_vi: "Đồng Nai",
    name_en: "Dong Nai",
    value: "dong_nai",
  },
  {
    name_vi: "Đồng Tháp",
    name_en: "Dong Thap",
    value: "dong_thap",
  },
  {
    name_vi: "Gia Lai",
    name_en: "Gia Lai",
    value: "gia_lai",
  },
  {
    name_vi: "Hà Tĩnh",
    name_en: "Ha Tinh",
    value: "ha_tinh",
  },
  {
    name_vi: "Hưng Yên",
    name_en: "Hung Yen",
    value: "hung_yen",
  },
  {
    name_vi: "Khánh Hòa",
    name_en: "Khanh Hoa",
    value: "khanh_hoa",
  },
  {
    name_vi: "Lai Châu",
    name_en: "Lai Chau",
    value: "lai_chau",
  },
  {
    name_vi: "Lâm Đồng",
    name_en: "Lam Dong",
    value: "lam_dong",
  },
  {
    name_vi: "Lạng Sơn",
    name_en: "Lang Son",
    value: "lang_son",
  },
  {
    name_vi: "Lào Cai",
    name_en: "Lao Cai",
    value: "lao_cai",
  },
  {
    name_vi: "Nghệ An",
    name_en: "Nghe An",
    value: "nghe_an",
  },
  {
    name_vi: "Ninh Bình",
    name_en: "Ninh Binh",
    value: "ninh_binh",
  },
  {
    name_vi: "Phú Thọ",
    name_en: "Phu Tho",
    value: "phu_tho",
  },
  {
    name_vi: "Quảng Ngãi",
    name_en: "Quang Ngai",
    value: "quang_ngai",
  },
  {
    name_vi: "Quảng Ninh",
    name_en: "Quang Ninh",
    value: "quang_ninh",
  },
  {
    name_vi: "Quảng Trị",
    name_en: "Quang Tri",
    value: "quang_tri",
  },
  {
    name_vi: "Sơn La",
    name_en: "Son La",
    value: "son_la",
  },
  {
    name_vi: "Tây Ninh",
    name_en: "Tay Ninh",
    value: "tay_ninh",
  },
  {
    name_vi: "Thái Nguyên",
    name_en: "Thai Nguyen",
    value: "thai_nguyen",
  },
  {
    name_vi: "Thanh Hóa",
    name_en: "Thanh Hoa",
    value: "thanh_hoa",
  },
  {
    name_vi: "Tuyên Quang",
    name_en: "Tuyen Quang",
    value: "tuyen_quang",
  },
  {
    name_vi: "Vĩnh Long",
    name_en: "Vinh Long",
    value: "vinh_long",
  },
  {
    name_vi: "Khác/Other",
    name_en: "Other",
    value: "other",
  },
];
export const CURRENCIES = [
  { label: "USD", value: SupportedCurrency.USD },
  { label: "VND", value: SupportedCurrency.VND },
];

export const TIMEZONES = {
  "Pacific/Niue": {
    offset: "-11:00",
    city: "Niue, Niue",
    value: "(GMT-11:00) Niue, Niue",
  },
  "Pacific/Pago_Pago": {
    offset: "-11:00",
    city: "Pago Pago, American Samoa",
    value: "(GMT-11:00) Pago Pago, American Samoa",
  },
  "America/Adak": {
    offset: "-10:00",
    city: "Adak, United States",
    value: "(GMT-10:00) Adak, United States",
  },
  "Pacific/Honolulu": {
    offset: "-10:00",
    city: "Honolulu, United States",
    value: "(GMT-10:00) Honolulu, United States",
  },
  "America/Anchorage": {
    offset: "-09:00",
    city: "Anchorage, United States",
    value: "(GMT-09:00) Anchorage, United States",
  },
  "America/Juneau": {
    offset: "-09:00",
    city: "Juneau, United States",
    value: "(GMT-09:00) Juneau, United States",
  },
  "America/Nome": {
    offset: "-09:00",
    city: "Nome, United States",
    value: "(GMT-09:00) Nome, United States",
  },
  "America/Sitka": {
    offset: "-09:00",
    city: "Sitka, United States",
    value: "(GMT-09:00) Sitka, United States",
  },
  "America/Yakutat": {
    offset: "-09:00",
    city: "Yakutat, United States",
    value: "(GMT-09:00) Yakutat, United States",
  },
  "Pacific/Gambier": {
    offset: "-09:00",
    city: "Gambier Islands, French Polynesia",
    value: "(GMT-09:00) Gambier Islands, French Polynesia",
  },
  "America/Los_Angeles": {
    offset: "-08:00",
    city: "Los Angeles, United States",
    value: "(GMT-08:00) Los Angeles, United States",
  },
  "America/Vancouver": {
    offset: "-08:00",
    city: "Vancouver, Canada",
    value: "(GMT-08:00) Vancouver, Canada",
  },
  "Pacific/Pitcairn": {
    offset: "-08:00",
    city: "Pitcairn Islands, Pitcairn Islands",
    value: "(GMT-08:00) Pitcairn Islands, Pitcairn Islands",
  },
  "America/Cambridge_Bay": {
    offset: "-07:00",
    city: "Cambridge Bay, Canada",
    value: "(GMT-07:00) Cambridge Bay, Canada",
  },
  "America/Chihuahua": {
    offset: "-07:00",
    city: "Chihuahua, Mexico",
    value: "(GMT-07:00) Chihuahua, Mexico",
  },
  "America/Dawson": {
    offset: "-07:00",
    city: "Dawson, Canada",
    value: "(GMT-07:00) Dawson, Canada",
  },
  "America/Dawson_Creek": {
    offset: "-07:00",
    city: "Dawson Creek, Canada",
    value: "(GMT-07:00) Dawson Creek, Canada",
  },
  "America/Denver": {
    offset: "-07:00",
    city: "Denver, United States",
    value: "(GMT-07:00) Denver, United States",
  },
  "America/Fort_Nelson": {
    offset: "-07:00",
    city: "Fort Nelson, Canada",
    value: "(GMT-07:00) Fort Nelson, Canada",
  },
  "America/Hermosillo": {
    offset: "-07:00",
    city: "Hermosillo, Mexico",
    value: "(GMT-07:00) Hermosillo, Mexico",
  },
  "America/Inuvik": {
    offset: "-07:00",
    city: "Inuvik, Canada",
    value: "(GMT-07:00) Inuvik, Canada",
  },
  "America/Mazatlan": {
    offset: "-07:00",
    city: "Mazatlan, Mexico",
    value: "(GMT-07:00) Mazatlan, Mexico",
  },
  "America/Ojinaga": {
    offset: "-07:00",
    city: "Ojinaga, Mexico",
    value: "(GMT-07:00) Ojinaga, Mexico",
  },
  "America/Phoenix": {
    offset: "-07:00",
    city: "Phoenix, United States",
    value: "(GMT-07:00) Phoenix, United States",
  },
  "America/Whitehorse": {
    offset: "-07:00",
    city: "Whitehorse, Canada",
    value: "(GMT-07:00) Whitehorse, Canada",
  },
  "America/Yellowknife": {
    offset: "-07:00",
    city: "Yellowknife, Canada",
    value: "(GMT-07:00) Yellowknife, Canada",
  },
  "America/Belize": {
    offset: "-06:00",
    city: "Belize, Belize",
    value: "(GMT-06:00) Belize, Belize",
  },
  "America/Boise": {
    offset: "-06:00",
    city: "Boise, United States",
    value: "(GMT-06:00) Boise, United States",
  },
  "America/Chicago": {
    offset: "-06:00",
    city: "Chicago, United States",
    value: "(GMT-06:00) Chicago, United States",
  },
  "America/Colima": {
    offset: "-06:00",
    city: "Colima, Mexico",
    value: "(GMT-06:00) Colima, Mexico",
  },
  "America/Costa_Rica": {
    offset: "-06:00",
    city: "Costa Rica, Costa Rica",
    value: "(GMT-06:00) Costa Rica, Costa Rica",
  },
  "America/Edmonton": {
    offset: "-06:00",
    city: "Edmonton, Canada",
    value: "(GMT-06:00) Edmonton, Canada",
  },
  "America/El_Salvador": {
    offset: "-06:00",
    city: "El Salvador, El Salvador",
    value: "(GMT-06:00) El Salvador, El Salvador",
  },
  "America/Galapagos": {
    offset: "-06:00",
    city: "Galapagos Islands, Ecuador",
    value: "(GMT-06:00) Galapagos Islands, Ecuador",
  },
  "America/Guatemala": {
    offset: "-06:00",
    city: "Guatemala, Guatemala",
    value: "(GMT-06:00) Guatemala, Guatemala",
  },
  "America/Knox": {
    offset: "-06:00",
    city: "Knox, United States",
    value: "(GMT-06:00) Knox, United States",
  },
  "America/Tell_City": {
    offset: "-06:00",
    city: "Tell City, United States",
    value: "(GMT-06:00) Tell City, United States",
  },
  "America/Managua": {
    offset: "-06:00",
    city: "Managua, Nicaragua",
    value: "(GMT-06:00) Managua, Nicaragua",
  },
  "America/Matamoros": {
    offset: "-06:00",
    city: "Matamoros, Mexico",
    value: "(GMT-06:00) Matamoros, Mexico",
  },
  "America/Mexico_City": {
    offset: "-06:00",
    city: "Mexico City, Mexico",
    value: "(GMT-06:00) Mexico City, Mexico",
  },
  "America/Monterrey": {
    offset: "-06:00",
    city: "Monterrey, Mexico",
    value: "(GMT-06:00) Monterrey, Mexico",
  },
  "America/Rainy_River": {
    offset: "-06:00",
    city: "Rainy River, Canada",
    value: "(GMT-06:00) Rainy River, Canada",
  },
  "America/Rankin_Inlet": {
    offset: "-06:00",
    city: "Rankin Inlet, Canada",
    value: "(GMT-06:00) Rankin Inlet, Canada",
  },
  "America/Regina": {
    offset: "-06:00",
    city: "Regina, Canada",
    value: "(GMT-06:00) Regina, Canada",
  },
  "America/Resolute": {
    offset: "-06:00",
    city: "Resolute, Canada",
    value: "(GMT-06:00) Resolute, Canada",
  },
  "America/Swift_Current": {
    offset: "-06:00",
    city: "Swift Current, Canada",
    value: "(GMT-06:00) Swift Current, Canada",
  },
  "America/Tegucigalpa": {
    offset: "-06:00",
    city: "Tegucigalpa, Honduras",
    value: "(GMT-06:00) Tegucigalpa, Honduras",
  },
  "America/Winnipeg": {
    offset: "-06:00",
    city: "Winnipeg, Canada",
    value: "(GMT-06:00) Winnipeg, Canada",
  },
  "Pacific/Easter": {
    offset: "-06:00",
    city: "Easter Island, Chile",
    value: "(GMT-06:00) Easter Island, Chile",
  },
  "Pacific/Galapagos": {
    offset: "-06:00",
    city: "Galapagos Islands, Ecuador",
    value: "(GMT-06:00) Galapagos Islands, Ecuador",
  },
  "America/Atikokan": {
    offset: "-05:00",
    city: "Atikokan, Canada",
    value: "(GMT-05:00) Atikokan, Canada",
  },
  "America/Bogota": {
    offset: "-05:00",
    city: "Bogota, Colombia",
    value: "(GMT-05:00) Bogota, Colombia",
  },
  "America/Cancun": {
    offset: "-05:00",
    city: "Cancun, Mexico",
    value: "(GMT-05:00) Cancun, Mexico",
  },
  "America/Cayman": {
    offset: "-05:00",
    city: "Cayman, Cayman Islands",
    value: "(GMT-05:00) Cayman, Cayman Islands",
  },
  "America/Detroit": {
    offset: "-05:00",
    city: "Detroit, United States",
    value: "(GMT-05:00) Detroit, United States",
  },
  "America/Eirunepe": {
    offset: "-05:00",
    city: "Eirunepe, Brazil",
    value: "(GMT-05:00) Eirunepe, Brazil",
  },
  "America/Guayaquil": {
    offset: "-05:00",
    city: "Guayaquil, Ecuador",
    value: "(GMT-05:00) Guayaquil, Ecuador",
  },
  "America/Havana": {
    offset: "-05:00",
    city: "Havana, Cuba",
    value: "(GMT-05:00) Havana, Cuba",
  },
  "America/Indianapolis": {
    offset: "-05:00",
    city: "Indianapolis, United States",
    value: "(GMT-05:00) Indianapolis, United States",
  },
  "America/Marengo": {
    offset: "-05:00",
    city: "Marengo, United States",
    value: "(GMT-05:00) Marengo, United States",
  },
  "America/Petersburg": {
    offset: "-05:00",
    city: "Petersburg, United States",
    value: "(GMT-05:00) Petersburg, United States",
  },
  "America/Vevay": {
    offset: "-05:00",
    city: "Vevay, United States",
    value: "(GMT-05:00) Vevay, United States",
  },
  "America/Vincennes": {
    offset: "-05:00",
    city: "Vincennes, United States",
    value: "(GMT-05:00) Vincennes, United States",
  },
  "America/Winamac": {
    offset: "-05:00",
    city: "Winamac, United States",
    value: "(GMT-05:00) Winamac, United States",
  },
  "America/Iqaluit": {
    offset: "-05:00",
    city: "Iqaluit, Canada",
    value: "(GMT-05:00) Iqaluit, Canada",
  },
  "America/Jamaica": {
    offset: "-05:00",
    city: "Jamaica, Jamaica",
    value: "(GMT-05:00) Jamaica, Jamaica",
  },
  "America/Louisville": {
    offset: "-05:00",
    city: "Louisville, United States",
    value: "(GMT-05:00) Louisville, United States",
  },
  "America/Monticello": {
    offset: "-05:00",
    city: "Monticello, United States",
    value: "(GMT-05:00) Monticello, United States",
  },
  "America/Lima": {
    offset: "-05:00",
    city: "Lima, Peru",
    value: "(GMT-05:00) Lima, Peru",
  },
  "America/Montreal": {
    offset: "-05:00",
    city: "Montreal, Canada",
    value: "(GMT-05:00) Montreal, Canada",
  },
  "America/Nassau": {
    offset: "-05:00",
    city: "Nassau, Bahamas",
    value: "(GMT-05:00) Nassau, Bahamas",
  },
  "America/New_York": {
    offset: "-05:00",
    city: "New York, United States",
    value: "(GMT-05:00) New York, United States",
  },
  "America/Nipigon": {
    offset: "-05:00",
    city: "Nipigon, Canada",
    value: "(GMT-05:00) Nipigon, Canada",
  },
  "America/Panama": {
    offset: "-05:00",
    city: "Panama, Panama",
    value: "(GMT-05:00) Panama, Panama",
  },
  "America/Pangnirtung": {
    offset: "-05:00",
    city: "Pangnirtung, Canada",
    value: "(GMT-05:00) Pangnirtung, Canada",
  },
  "America/Port-au-Prince": {
    offset: "-05:00",
    city: "Port-au-Prince, Haiti",
    value: "(GMT-05:00) Port-au-Prince, Haiti",
  },
  "America/Rio_Branco": {
    offset: "-05:00",
    city: "Rio Branco, Brazil",
    value: "(GMT-05:00) Rio Branco, Brazil",
  },
  "America/Timberlea": {
    offset: "-05:00",
    city: "Timberlea, Canada",
    value: "(GMT-05:00) Timberlea, Canada",
  },
  "America/Toronto": {
    offset: "-05:00",
    city: "Toronto, Canada",
    value: "(GMT-05:00) Toronto, Canada",
  },
  "America/Anguilla": {
    offset: "-04:00",
    city: "Anguilla, Anguilla",
    value: "(GMT-04:00) Anguilla, Anguilla",
  },
  "America/Antigua": {
    offset: "-04:00",
    city: "Antigua, Antigua and Barbuda",
    value: "(GMT-04:00) Antigua, Antigua and Barbuda",
  },
  "America/Aruba": {
    offset: "-04:00",
    city: "Aruba, Aruba",
    value: "(GMT-04:00) Aruba, Aruba",
  },
  "America/Asuncion": {
    offset: "-04:00",
    city: "Asuncion, Paraguay",
    value: "(GMT-04:00) Asuncion, Paraguay",
  },
  "America/Barbados": {
    offset: "-04:00",
    city: "Barbados, Barbados",
    value: "(GMT-04:00) Barbados, Barbados",
  },
  "America/Boa_Vista": {
    offset: "-04:00",
    city: "Boa Vista, Brazil",
    value: "(GMT-04:00) Boa Vista, Brazil",
  },
  "America/Campo_Grande": {
    offset: "-04:00",
    city: "Campo Grande, Brazil",
    value: "(GMT-04:00) Campo Grande, Brazil",
  },
  "America/Caracas": {
    offset: "-04:00",
    city: "Caracas, Venezuela",
    value: "(GMT-04:00) Caracas, Venezuela",
  },
  "America/Cuiaba": {
    offset: "-04:00",
    city: "Cuiaba, Brazil",
    value: "(GMT-04:00) Cuiaba, Brazil",
  },
  "America/Curacao": {
    offset: "-04:00",
    city: "Curacao, Curacao",
    value: "(GMT-04:00) Curacao, Curacao",
  },
  "America/Dominica": {
    offset: "-04:00",
    city: "Dominica, Dominica",
    value: "(GMT-04:00) Dominica, Dominica",
  },
  "America/Grenada": {
    offset: "-04:00",
    city: "Grenada, Grenada",
    value: "(GMT-04:00) Grenada, Grenada",
  },
  "America/Guadeloupe": {
    offset: "-04:00",
    city: "Guadeloupe, Guadeloupe",
    value: "(GMT-04:00) Guadeloupe, Guadeloupe",
  },
  "America/Guyana": {
    offset: "-04:00",
    city: "Guyana, Guyana",
    value: "(GMT-04:00) Guyana, Guyana",
  },
  "America/Halifax": {
    offset: "-04:00",
    city: "Halifax, Canada",
    value: "(GMT-04:00) Halifax, Canada",
  },
  "America/Kralendijk": {
    offset: "-04:00",
    city: "Kralendijk, Bonaire",
    value: "(GMT-04:00) Kralendijk, Bonaire",
  },
  "America/La_Paz": {
    offset: "-04:00",
    city: "La Paz, Bolivia",
    value: "(GMT-04:00) La Paz, Bolivia",
  },
  "America/Manaus": {
    offset: "-04:00",
    city: "Manaus, Brazil",
    value: "(GMT-04:00) Manaus, Brazil",
  },
  "America/Martinique": {
    offset: "-04:00",
    city: "Martinique, Martinique",
    value: "(GMT-04:00) Martinique, Martinique",
  },
  "America/Porto_Velho": {
    offset: "-04:00",
    city: "Porto Velho, Brazil",
    value: "(GMT-04:00) Porto Velho, Brazil",
  },
  "America/Puerto_Rico": {
    offset: "-04:00",
    city: "Puerto Rico, Puerto Rico",
    value: "(GMT-04:00) Puerto Rico, Puerto Rico",
  },
  "America/St_Barthelemy": {
    offset: "-04:00",
    city: "St Barthelemy, Saint Barthelemy",
    value: "(GMT-04:00) St Barthelemy, Saint Barthelemy",
  },
  "America/St_Kitts": {
    offset: "-04:00",
    city: "St Kitts, Saint Kitts and Nevis",
    value: "(GMT-04:00) St Kitts, Saint Kitts and Nevis",
  },
  "America/St_Lucia": {
    offset: "-04:00",
    city: "St Lucia, Saint Lucia",
    value: "(GMT-04:00) St Lucia, Saint Lucia",
  },
  "America/St_Thomas": {
    offset: "-04:00",
    city: "St Thomas, Virgin Islands",
    value: "(GMT-04:00) St Thomas, Virgin Islands",
  },
  "America/St_Vincent": {
    offset: "-04:00",
    city: "St Vincent, Saint Vincent and the Grenadines",
    value: "(GMT-04:00) St Vincent, Saint Vincent and the Grenadines",
  },
  "America/Thule": {
    offset: "-04:00",
    city: "Thule, Greenland",
    value: "(GMT-04:00) Thule, Greenland",
  },
  "America/Tortola": {
    offset: "-04:00",
    city: "Tortola, British Virgin Islands",
    value: "(GMT-04:00) Tortola, British Virgin Islands",
  },
  "America/Araguaina": {
    offset: "-03:00",
    city: "Araguaina, Brazil",
    value: "(GMT-03:00) Araguaina, Brazil",
  },
  "America/Buenos_Aires": {
    offset: "-03:00",
    city: "Buenos Aires, Argentina",
    value: "(GMT-03:00) Buenos Aires, Argentina",
  },
  "America/Catamarca": {
    offset: "-03:00",
    city: "Catamarca, Argentina",
    value: "(GMT-03:00) Catamarca, Argentina",
  },
  "America/ComodRivadavia": {
    offset: "-03:00",
    city: "ComodRivadavia, Argentina",
    value: "(GMT-03:00) ComodRivadavia, Argentina",
  },
  "America/Jujuy": {
    offset: "-03:00",
    city: "Jujuy, Argentina",
    value: "(GMT-03:00) Jujuy, Argentina",
  },
  "America/La_Rioja": {
    offset: "-03:00",
    city: "La Rioja, Argentina",
    value: "(GMT-03:00) La Rioja, Argentina",
  },
  "America/Mendoza": {
    offset: "-03:00",
    city: "Mendoza, Argentina",
    value: "(GMT-03:00) Mendoza, Argentina",
  },
  "America/Rio_Gallegos": {
    offset: "-03:00",
    city: "Rio Gallegos, Argentina",
    value: "(GMT-03:00) Rio Gallegos, Argentina",
  },
  "America/Salta": {
    offset: "-03:00",
    city: "Salta, Argentina",
    value: "(GMT-03:00) Salta, Argentina",
  },
  "America/San_Juan": {
    offset: "-03:00",
    city: "San Juan, Argentina",
    value: "(GMT-03:00) San Juan, Argentina",
  },
  "America/San_Luis": {
    offset: "-03:00",
    city: "San Luis, Argentina",
    value: "(GMT-03:00) San Luis, Argentina",
  },
  "America/Tucuman": {
    offset: "-03:00",
    city: "Tucuman, Argentina",
    value: "(GMT-03:00) Tucuman, Argentina",
  },
  "America/Ushuaia": {
    offset: "-03:00",
    city: "Ushuaia, Argentina",
    value: "(GMT-03:00) Ushuaia, Argentina",
  },
  "America/Bahia": {
    offset: "-03:00",
    city: "Bahia, Brazil",
    value: "(GMT-03:00) Bahia, Brazil",
  },
  "America/Belem": {
    offset: "-03:00",
    city: "Belem, Brazil",
    value: "(GMT-03:00) Belem, Brazil",
  },
  "America/Cayenne": {
    offset: "-03:00",
    city: "Cayenne, French Guiana",
    value: "(GMT-03:00) Cayenne, French Guiana",
  },
  "America/Fortaleza": {
    offset: "-03:00",
    city: "Fortaleza, Brazil",
    value: "(GMT-03:00) Fortaleza, Brazil",
  },
  "America/Maceio": {
    offset: "-03:00",
    city: "Maceio, Brazil",
    value: "(GMT-03:00) Maceio, Brazil",
  },
  "America/Miquelon": {
    offset: "-03:00",
    city: "Miquelon, Saint Pierre and Miquelon",
    value: "(GMT-03:00) Miquelon, Saint Pierre and Miquelon",
  },
  "America/Montevideo": {
    offset: "-03:00",
    city: "Montevideo, Uruguay",
    value: "(GMT-03:00) Montevideo, Uruguay",
  },
  "America/Paramaribo": {
    offset: "-03:00",
    city: "Paramaribo, Suriname",
    value: "(GMT-03:00) Paramaribo, Suriname",
  },
  "America/Punta_Arenas": {
    offset: "-03:00",
    city: "Punta Arenas, Chile",
    value: "(GMT-03:00) Punta Arenas, Chile",
  },
  "America/Recife": {
    offset: "-03:00",
    city: "Recife, Brazil",
    value: "(GMT-03:00) Recife, Brazil",
  },
  "America/Santarem": {
    offset: "-03:00",
    city: "Santarem, Brazil",
    value: "(GMT-03:00) Santarem, Brazil",
  },
  "America/Sao_Paulo": {
    offset: "-03:00",
    city: "Sao Paulo, Brazil",
    value: "(GMT-03:00) Sao Paulo, Brazil",
  },
  "Antarctica/Palmer": {
    offset: "-03:00",
    city: "Palmer, Antarctica",
    value: "(GMT-03:00) Palmer, Antarctica",
  },
  "Antarctica/Rothera": {
    offset: "-03:00",
    city: "Rothera, Antarctica",
    value: "(GMT-03:00) Rothera, Antarctica",
  },
  "Atlantic/Bermuda": {
    offset: "-03:00",
    city: "Bermuda, Bermuda",
    value: "(GMT-03:00) Bermuda, Bermuda",
  },
  "America/St_Johns": {
    offset: "-03:30",
    city: "St Johns, Canada",
    value: "(GMT-03:30) St Johns, Canada",
  },
  "America/Noronha": {
    offset: "-02:00",
    city: "Noronha, Brazil",
    value: "(GMT-02:00) Noronha, Brazil",
  },
  "Atlantic/South_Georgia": {
    offset: "-02:00",
    city: "South Georgia, South Georgia and the South Sandwich Islands",
    value:
      "(GMT-02:00) South Georgia, South Georgia and the South Sandwich Islands",
  },
  "America/Scoresbysund": {
    offset: "-01:00",
    city: "Scoresbysund, Greenland",
    value: "(GMT-01:00) Scoresbysund, Greenland",
  },
  "Atlantic/Azores": {
    offset: "-01:00",
    city: "Azores, Portugal",
    value: "(GMT-01:00) Azores, Portugal",
  },
  "Atlantic/Cape_Verde": {
    offset: "-01:00",
    city: "Cape Verde, Cape Verde",
    value: "(GMT-01:00) Cape Verde, Cape Verde",
  },
  "Africa/Abidjan": {
    offset: "+00:00",
    city: "Abidjan, C\u00f4te d'Ivoire",
    value: "(GMT+00:00) Abidjan, C\u00f4te d'Ivoire",
  },
  "Africa/Accra": {
    offset: "+00:00",
    city: "Accra, Ghana",
    value: "(GMT+00:00) Accra, Ghana",
  },
  "Africa/Bamako": {
    offset: "+00:00",
    city: "Bamako, Mali",
    value: "(GMT+00:00) Bamako, Mali",
  },
  "Africa/Banjul": {
    offset: "+00:00",
    city: "Banjul, Gambia",
    value: "(GMT+00:00) Banjul, Gambia",
  },
  "Africa/Bissau": {
    offset: "+00:00",
    city: "Bissau, Guinea-Bissau",
    value: "(GMT+00:00) Bissau, Guinea-Bissau",
  },
  "Africa/Conakry": {
    offset: "+00:00",
    city: "Conakry, Guinea",
    value: "(GMT+00:00) Conakry, Guinea",
  },
  "Africa/Dakar": {
    offset: "+00:00",
    city: "Dakar, Senegal",
    value: "(GMT+00:00) Dakar, Senegal",
  },
  "Africa/Monrovia": {
    offset: "+00:00",
    city: "Monrovia, Liberia",
    value: "(GMT+00:00) Monrovia, Liberia",
  },
  "Africa/Nouakchott": {
    offset: "+00:00",
    city: "Nouakchott, Mauritania",
    value: "(GMT+00:00) Nouakchott, Mauritania",
  },
  "Africa/Ouagadougou": {
    offset: "+00:00",
    city: "Ouagadougou, Burkina Faso",
    value: "(GMT+00:00) Ouagadougou, Burkina Faso",
  },
  "Africa/Sao_Tome": {
    offset: "+00:00",
    city: "Sao Tome, S\u00e3o Tom\u00e9 and Pr\u00edncipe",
    value: "(GMT+00:00) Sao Tome, S\u00e3o Tom\u00e9 and Pr\u00edncipe",
  },
  "Africa/Timbuktu": {
    offset: "+00:00",
    city: "Timbuktu, Mali",
    value: "(GMT+00:00) Timbuktu, Mali",
  },
  "America/Danmarkshavn": {
    offset: "+00:00",
    city: "Danmarkshavn, Greenland",
    value: "(GMT+00:00) Danmarkshavn, Greenland",
  },
  "Atlantic/Canary": {
    offset: "+00:00",
    city: "Canary, Spain",
    value: "(GMT+00:00) Canary, Spain",
  },
  "Atlantic/Faeroe": {
    offset: "+00:00",
    city: "Faeroe Islands, Faroe Islands",
    value: "(GMT+00:00) Faeroe Islands, Faroe Islands",
  },
  "Atlantic/Madeira": {
    offset: "+00:00",
    city: "Madeira, Portugal",
    value: "(GMT+00:00) Madeira, Portugal",
  },
  "Atlantic/Reykjavik": {
    offset: "+00:00",
    city: "Reykjavik, Iceland",
    value: "(GMT+00:00) Reykjavik, Iceland",
  },
  "Europe/Dublin": {
    offset: "+00:00",
    city: "Dublin, Ireland",
    value: "(GMT+00:00) Dublin, Ireland",
  },
  "Europe/Guernsey": {
    offset: "+00:00",
    city: "Guernsey, Guernsey",
    value: "(GMT+00:00) Guernsey, Guernsey",
  },
  "Europe/Isle_of_Man": {
    offset: "+00:00",
    city: "Isle of Man, Isle of Man",
    value: "(GMT+00:00) Isle of Man, Isle of Man",
  },
  "Europe/Jersey": {
    offset: "+00:00",
    city: "Jersey, Jersey",
    value: "(GMT+00:00) Jersey, Jersey",
  },
  "Europe/Lisbon": {
    offset: "+00:00",
    city: "Lisbon, Portugal",
    value: "(GMT+00:00) Lisbon, Portugal",
  },
  "Europe/London": {
    offset: "+00:00",
    city: "London, United Kingdom",
    value: "(GMT+00:00) London, United Kingdom",
  },
  "Africa/Algiers": {
    offset: "+01:00",
    city: "Algiers, Algeria",
    value: "(GMT+01:00) Algiers, Algeria",
  },
  "Africa/Brazzaville": {
    offset: "+01:00",
    city: "Brazzaville, Congo",
    value: "(GMT+01:00) Brazzaville, Congo",
  },
  "Africa/Casablanca": {
    offset: "+01:00",
    city: "Casablanca, Morocco",
    value: "(GMT+01:00) Casablanca, Morocco",
  },
  "Africa/Ceuta": {
    offset: "+01:00",
    city: "Ceuta, Spain",
    value: "(GMT+01:00) Ceuta, Spain",
  },
  "Africa/Douala": {
    offset: "+01:00",
    city: "Douala, Cameroon",
    value: "(GMT+01:00) Douala, Cameroon",
  },
  "Africa/Kinshasa": {
    offset: "+01:00",
    city: "Kinshasa, Democratic Republic of the Congo",
    value: "(GMT+01:00) Kinshasa, Democratic Republic of the Congo",
  },
  "Africa/Lagos": {
    offset: "+01:00",
    city: "Lagos, Nigeria",
    value: "(GMT+01:00) Lagos, Nigeria",
  },
  "Africa/Libreville": {
    offset: "+01:00",
    city: "Libreville, Gabon",
    value: "(GMT+01:00) Libreville, Gabon",
  },
  "Africa/Malabo": {
    offset: "+01:00",
    city: "Malabo, Equatorial Guinea",
    value: "(GMT+01:00) Malabo, Equatorial Guinea",
  },
  "Africa/Ndjamena": {
    offset: "+01:00",
    city: "Ndjamena, Chad",
    value: "(GMT+01:00) Ndjamena, Chad",
  },
  "Africa/Niamey": {
    offset: "+01:00",
    city: "Niamey, Niger",
    value: "(GMT+01:00) Niamey, Niger",
  },
  "Africa/Porto-Novo": {
    offset: "+01:00",
    city: "Porto-Novo, Benin",
    value: "(GMT+01:00) Porto-Novo, Benin",
  },
  "Africa/Tunis": {
    offset: "+01:00",
    city: "Tunis, Tunisia",
    value: "(GMT+01:00) Tunis, Tunisia",
  },
  "Africa/Windhoek": {
    offset: "+01:00",
    city: "Windhoek, Namibia",
    value: "(GMT+01:00) Windhoek, Namibia",
  },
  "Europe/Andorra": {
    offset: "+01:00",
    city: "Andorra la Vella, Andorra",
    value: "(GMT+01:00) Andorra la Vella, Andorra",
  },
  "Europe/Amsterdam": {
    offset: "+01:00",
    city: "Amsterdam, Netherlands",
    value: "(GMT+01:00) Amsterdam, Netherlands",
  },
  "Europe/Belgrade": {
    offset: "+01:00",
    city: "Belgrade, Serbia",
    value: "(GMT+01:00) Belgrade, Serbia",
  },
  "Europe/Berlin": {
    offset: "+01:00",
    city: "Berlin, Germany",
    value: "(GMT+01:00) Berlin, Germany",
  },
  "Europe/Bratislava": {
    offset: "+01:00",
    city: "Bratislava, Slovakia",
    value: "(GMT+01:00) Bratislava, Slovakia",
  },
  "Europe/Brussels": {
    offset: "+01:00",
    city: "Brussels, Belgium",
    value: "(GMT+01:00) Brussels, Belgium",
  },
  "Europe/Budapest": {
    offset: "+01:00",
    city: "Budapest, Hungary",
    value: "(GMT+01:00) Budapest, Hungary",
  },
  "Europe/Copenhagen": {
    offset: "+01:00",
    city: "Copenhagen, Denmark",
    value: "(GMT+01:00) Copenhagen, Denmark",
  },
  "Europe/Gibraltar": {
    offset: "+01:00",
    city: "Gibraltar, Gibraltar",
    value: "(GMT+01:00) Gibraltar, Gibraltar",
  },
  "Europe/Ljubljana": {
    offset: "+01:00",
    city: "Ljubljana, Slovenia",
    value: "(GMT+01:00) Ljubljana, Slovenia",
  },
  "Europe/Luxembourg": {
    offset: "+01:00",
    city: "Luxembourg, Luxembourg",
    value: "(GMT+01:00) Luxembourg, Luxembourg",
  },
  "Europe/Madrid": {
    offset: "+01:00",
    city: "Madrid, Spain",
    value: "(GMT+01:00) Madrid, Spain",
  },
  "Europe/Malta": {
    offset: "+01:00",
    city: "Malta, Malta",
    value: "(GMT+01:00) Malta, Malta",
  },
  "Europe/Monaco": {
    offset: "+01:00",
    city: "Monaco, Monaco",
    value: "(GMT+01:00) Monaco, Monaco",
  },
  "Europe/Oslo": {
    offset: "+01:00",
    city: "Oslo, Norway",
    value: "(GMT+01:00) Oslo, Norway",
  },
  "Europe/Paris": {
    offset: "+01:00",
    city: "Paris, France",
    value: "(GMT+01:00) Paris, France",
  },
  "Europe/Podgorica": {
    offset: "+01:00",
    city: "Podgorica, Montenegro",
    value: "(GMT+01:00) Podgorica, Montenegro",
  },
  "Europe/Prague": {
    offset: "+01:00",
    city: "Prague, Czech Republic",
    value: "(GMT+01:00) Prague, Czech Republic",
  },
  "Europe/Rome": {
    offset: "+01:00",
    city: "Rome, Italy",
    value: "(GMT+01:00) Rome, Italy",
  },
  "Europe/San_Marino": {
    offset: "+01:00",
    city: "San Marino, San Marino",
    value: "(GMT+01:00) San Marino, San Marino",
  },
  "Europe/Sarajevo": {
    offset: "+01:00",
    city: "Sarajevo, Bosnia and Herzegovina",
    value: "(GMT+01:00) Sarajevo, Bosnia and Herzegovina",
  },
  "Europe/Skopje": {
    offset: "+01:00",
    city: "Skopje, North Macedonia",
    value: "(GMT+01:00) Skopje, North Macedonia",
  },
  "Europe/Stockholm": {
    offset: "+01:00",
    city: "Stockholm, Sweden",
    value: "(GMT+01:00) Stockholm, Sweden",
  },
  "Europe/Tirane": {
    offset: "+01:00",
    city: "Tirana, Albania",
    value: "(GMT+01:00) Tirana, Albania",
  },
  "Europe/Vaduz": {
    offset: "+01:00",
    city: "Vaduz, Liechtenstein",
    value: "(GMT+01:00) Vaduz, Liechtenstein",
  },
  "Europe/Vienna": {
    offset: "+01:00",
    city: "Vienna, Austria",
    value: "(GMT+01:00) Vienna, Austria",
  },
  "Europe/Warsaw": {
    offset: "+01:00",
    city: "Warsaw, Poland",
    value: "(GMT+01:00) Warsaw, Poland",
  },
  "Europe/Zagreb": {
    offset: "+01:00",
    city: "Zagreb, Croatia",
    value: "(GMT+01:00) Zagreb, Croatia",
  },
  "Europe/Zurich": {
    offset: "+01:00",
    city: "Zurich, Switzerland",
    value: "(GMT+01:00) Zurich, Switzerland",
  },
  "Africa/Blantyre": {
    offset: "+02:00",
    city: "Blantyre, Malawi",
    value: "(GMT+02:00) Blantyre, Malawi",
  },
  "Africa/Bujumbura": {
    offset: "+02:00",
    city: "Bujumbura, Burundi",
    value: "(GMT+02:00) Bujumbura, Burundi",
  },
  "Africa/Cairo": {
    offset: "+02:00",
    city: "Cairo, Egypt",
    value: "(GMT+02:00) Cairo, Egypt",
  },
  "Africa/Lubumbashi": {
    offset: "+02:00",
    city: "Lubumbashi, Democratic Republic of the Congo",
    value: "(GMT+02:00) Lubumbashi, Democratic Republic of the Congo",
  },
  "Africa/Lusaka": {
    offset: "+02:00",
    city: "Lusaka, Zambia",
    value: "(GMT+02:00) Lusaka, Zambia",
  },
  "Africa/Maputo": {
    offset: "+02:00",
    city: "Maputo, Mozambique",
    value: "(GMT+02:00) Maputo, Mozambique",
  },
  "Africa/Maseru": {
    offset: "+02:00",
    city: "Maseru, Lesotho",
    value: "(GMT+02:00) Maseru, Lesotho",
  },
  "Africa/Mbabane": {
    offset: "+02:00",
    city: "Mbabane, Eswatini",
    value: "(GMT+02:00) Mbabane, Eswatini",
  },
  "Africa/Tripoli": {
    offset: "+02:00",
    city: "Tripoli, Libya",
    value: "(GMT+02:00) Tripoli, Libya",
  },
  "Antarctica/Troll": {
    offset: "+02:00",
    city: "Troll, Antarctica",
    value: "(GMT+02:00) Troll, Antarctica",
  },
  "Asia/Gaza": {
    offset: "+02:00",
    city: "Gaza, Palestinian Territory",
    value: "(GMT+02:00) Gaza, Palestinian Territory",
  },
  "Asia/Hebron": {
    offset: "+02:00",
    city: "Hebron, Palestinian Territory",
    value: "(GMT+02:00) Hebron, Palestinian Territory",
  },
  "Asia/Nicosia": {
    offset: "+02:00",
    city: "Nicosia, Cyprus",
    value: "(GMT+02:00) Nicosia, Cyprus",
  },
  "Europe/Athens": {
    offset: "+02:00",
    city: "Athens, Greece",
    value: "(GMT+02:00) Athens, Greece",
  },
  "Europe/Bucharest": {
    offset: "+02:00",
    city: "Bucharest, Romania",
    value: "(GMT+02:00) Bucharest, Romania",
  },
  "Europe/Chisinau": {
    offset: "+02:00",
    city: "Chisinau, Moldova",
    value: "(GMT+02:00) Chisinau, Moldova",
  },
  "Europe/Helsinki": {
    offset: "+02:00",
    city: "Helsinki, Finland",
    value: "(GMT+02:00) Helsinki, Finland",
  },
  "Europe/Kaliningrad": {
    offset: "+02:00",
    city: "Kaliningrad, Russia",
    value: "(GMT+02:00) Kaliningrad, Russia",
  },
  "Europe/Kiev": {
    offset: "+02:00",
    city: "Kiev, Ukraine",
    value: "(GMT+02:00) Kiev, Ukraine",
  },
  "Europe/Riga": {
    offset: "+02:00",
    city: "Riga, Latvia",
    value: "(GMT+02:00) Riga, Latvia",
  },
  "Europe/Sofia": {
    offset: "+02:00",
    city: "Sofia, Bulgaria",
    value: "(GMT+02:00) Sofia, Bulgaria",
  },
  "Europe/Tallinn": {
    offset: "+02:00",
    city: "Tallinn, Estonia",
    value: "(GMT+02:00) Tallinn, Estonia",
  },
  "Europe/Vilnius": {
    offset: "+02:00",
    city: "Vilnius, Lithuania",
    value: "(GMT+02:00) Vilnius, Lithuania",
  },
  "Africa/Addis_Ababa": {
    offset: "+03:00",
    city: "Addis Ababa, Ethiopia",
    value: "(GMT+03:00) Addis Ababa, Ethiopia",
  },
  "Africa/Dar_es_Salaam": {
    offset: "+03:00",
    city: "Dar es Salaam, Tanzania",
    value: "(GMT+03:00) Dar es Salaam, Tanzania",
  },
  "Africa/Djibouti": {
    offset: "+03:00",
    city: "Djibouti, Djibouti",
    value: "(GMT+03:00) Djibouti, Djibouti",
  },
  "Africa/Mogadishu": {
    offset: "+03:00",
    city: "Mogadishu, Somalia",
    value: "(GMT+03:00) Mogadishu, Somalia",
  },
  "Africa/Nairobi": {
    offset: "+03:00",
    city: "Nairobi, Kenya",
    value: "(GMT+03:00) Nairobi, Kenya",
  },
  "Asia/Aden": {
    offset: "+03:00",
    city: "Aden, Yemen",
    value: "(GMT+03:00) Aden, Yemen",
  },
  "Asia/Amman": {
    offset: "+03:00",
    city: "Amman, Jordan",
    value: "(GMT+03:00) Amman, Jordan",
  },
  "Asia/Beirut": {
    offset: "+03:00",
    city: "Beirut, Lebanon",
    value: "(GMT+03:00) Beirut, Lebanon",
  },
  "Asia/Damascus": {
    offset: "+03:00",
    city: "Damascus, Syria",
    value: "(GMT+03:00) Damascus, Syria",
  },
  "Asia/Qatar": {
    offset: "+03:00",
    city: "Qatar, Qatar",
    value: "(GMT+03:00) Qatar, Qatar",
  },
  "Asia/Riyadh": {
    offset: "+03:00",
    city: "Riyadh, Saudi Arabia",
    value: "(GMT+03:00) Riyadh, Saudi Arabia",
  },
  "Europe/Istanbul": {
    offset: "+03:00",
    city: "Istanbul, Turkey",
    value: "(GMT+03:00) Istanbul, Turkey",
  },
  "Europe/Moscow": {
    offset: "+03:00",
    city: "Moscow, Russia",
    value: "(GMT+03:00) Moscow, Russia",
  },
  "Europe/Volgograd": {
    offset: "+03:00",
    city: "Volgograd, Russia",
    value: "(GMT+03:00) Volgograd, Russia",
  },
  "Indian/Antananarivo": {
    offset: "+03:00",
    city: "Antananarivo, Madagascar",
    value: "(GMT+03:00) Antananarivo, Madagascar",
  },
  "Asia/Tehran": {
    offset: "+03:30",
    city: "Tehran, Iran",
    value: "(GMT+03:30) Tehran, Iran",
  },
  "Asia/Dubai": {
    offset: "+04:00",
    city: "Dubai, United Arab Emirates",
    value: "(GMT+04:00) Dubai, United Arab Emirates",
  },
  "Asia/Muscat": {
    offset: "+04:00",
    city: "Muscat, Oman",
    value: "(GMT+04:00) Muscat, Oman",
  },
  "Asia/Tbilisi": {
    offset: "+04:00",
    city: "Tbilisi, Georgia",
    value: "(GMT+04:00) Tbilisi, Georgia",
  },
  "Asia/Yerevan": {
    offset: "+04:00",
    city: "Yerevan, Armenia",
    value: "(GMT+04:00) Yerevan, Armenia",
  },
  "Indian/Mauritius": {
    offset: "+04:00",
    city: "Mauritius, Mauritius",
    value: "(GMT+04:00) Mauritius, Mauritius",
  },
  "Indian/Reunion": {
    offset: "+04:00",
    city: "Reunion, R\u00e9union",
    value: "(GMT+04:00) Reunion, R\u00e9union",
  },
  "Asia/Kabul": {
    offset: "+04:30",
    city: "Kabul, Afghanistan",
    value: "(GMT+04:30) Kabul, Afghanistan",
  },
  "Asia/Aqtau": {
    offset: "+05:00",
    city: "Aqtau, Kazakhstan",
    value: "(GMT+05:00) Aqtau, Kazakhstan",
  },
  "Asia/Aqtobe": {
    offset: "+05:00",
    city: "Aqtobe, Kazakhstan",
    value: "(GMT+05:00) Aqtobe, Kazakhstan",
  },
  "Asia/Karachi": {
    offset: "+05:00",
    city: "Karachi, Pakistan",
    value: "(GMT+05:00) Karachi, Pakistan",
  },
  "Asia/Oral": {
    offset: "+05:00",
    city: "Oral, Kazakhstan",
    value: "(GMT+05:00) Oral, Kazakhstan",
  },
  "Asia/Samarkand": {
    offset: "+05:00",
    city: "Samarkand, Uzbekistan",
    value: "(GMT+05:00) Samarkand, Uzbekistan",
  },
  "Asia/Tashkent": {
    offset: "+05:00",
    city: "Tashkent, Uzbekistan",
    value: "(GMT+05:00) Tashkent, Uzbekistan",
  },
  "Asia/Yekaterinburg": {
    offset: "+05:00",
    city: "Yekaterinburg, Russia",
    value: "(GMT+05:00) Yekaterinburg, Russia",
  },
  "Indian/Kerguelen": {
    offset: "+05:00",
    city: "Kerguelen, French Southern Territories",
    value: "(GMT+05:00) Kerguelen, French Southern Territories",
  },
  "Indian/Maldives": {
    offset: "+05:00",
    city: "Maldives, Maldives",
    value: "(GMT+05:00) Maldives, Maldives",
  },
  "Asia/Calcutta": {
    offset: "+05:30",
    city: "Kolkata, India",
    value: "(GMT+05:30) Kolkata, India",
  },
  "Asia/Colombo": {
    offset: "+05:30",
    city: "Colombo, Sri Lanka",
    value: "(GMT+05:30) Colombo, Sri Lanka",
  },
  "Asia/Kolkata": {
    offset: "+05:30",
    city: "Kolkata, India",
    value: "(GMT+05:30) Kolkata, India",
  },
  "Asia/Kathmandu": {
    offset: "+05:45",
    city: "Kathmandu, Nepal",
    value: "(GMT+05:45) Kathmandu, Nepal",
  },
  "Asia/Almaty": {
    offset: "+06:00",
    city: "Almaty, Kazakhstan",
    value: "(GMT+06:00) Almaty, Kazakhstan",
  },
  "Asia/Bishkek": {
    offset: "+06:00",
    city: "Bishkek, Kyrgyzstan",
    value: "(GMT+06:00) Bishkek, Kyrgyzstan",
  },
  "Asia/Dhaka": {
    offset: "+06:00",
    city: "Dhaka, Bangladesh",
    value: "(GMT+06:00) Dhaka, Bangladesh",
  },
  "Asia/Omsk": {
    offset: "+06:00",
    city: "Omsk, Russia",
    value: "(GMT+06:00) Omsk, Russia",
  },
  "Asia/Qyzylorda": {
    offset: "+06:00",
    city: "Qyzylorda, Kazakhstan",
    value: "(GMT+06:00) Qyzylorda, Kazakhstan",
  },
  "Asia/Urumqi": {
    offset: "+06:00",
    city: "Urumqi, China",
    value: "(GMT+06:00) Urumqi, China",
  },
  "Indian/Chagos": {
    offset: "+06:00",
    city: "Chagos, British Indian Ocean Territory",
    value: "(GMT+06:00) Chagos, British Indian Ocean Territory",
  },
  "Asia/Yangon": {
    offset: "+06:30",
    city: "Yangon, Myanmar",
    value: "(GMT+06:30) Yangon, Myanmar",
  },
  "Asia/Barnaul": {
    offset: "+07:00",
    city: "Barnaul, Russia",
    value: "(GMT+07:00) Barnaul, Russia",
  },
  "Asia/Ho_Chi_Minh": {
    offset: "+07:00",
    city: "Ho Chi Minh, Vietnam",
    value: "(GMT+07:00) Ho Chi Minh, Vietnam",
  },
  "Asia/Jakarta": {
    offset: "+07:00",
    city: "Jakarta, Indonesia",
    value: "(GMT+07:00) Jakarta, Indonesia",
  },
  "Asia/Krasnoyarsk": {
    offset: "+07:00",
    city: "Krasnoyarsk, Russia",
    value: "(GMT+07:00) Krasnoyarsk, Russia",
  },
  "Asia/Novokuznetsk": {
    offset: "+07:00",
    city: "Novokuznetsk, Russia",
    value: "(GMT+07:00) Novokuznetsk, Russia",
  },
  "Asia/Novosibirsk": {
    offset: "+07:00",
    city: "Novosibirsk, Russia",
    value: "(GMT+07:00) Novosibirsk, Russia",
  },
  "Asia/Phnom_Penh": {
    offset: "+07:00",
    city: "Phnom Penh, Cambodia",
    value: "(GMT+07:00) Phnom Penh, Cambodia",
  },
  "Asia/Pontianak": {
    offset: "+07:00",
    city: "Pontianak, Indonesia",
    value: "(GMT+07:00) Pontianak, Indonesia",
  },
  "Asia/Vientiane": {
    offset: "+07:00",
    city: "Vientiane, Laos",
    value: "(GMT+07:00) Vientiane, Laos",
  },
  "Asia/Brunei": {
    offset: "+08:00",
    city: "Brunei, Brunei",
    value: "(GMT+08:00) Brunei, Brunei",
  },
  "Asia/Chongqing": {
    offset: "+08:00",
    city: "Chongqing, China",
    value: "(GMT+08:00) Chongqing, China",
  },
  "Asia/Hong_Kong": {
    offset: "+08:00",
    city: "Hong Kong, Hong Kong",
    value: "(GMT+08:00) Hong Kong, Hong Kong",
  },
  "Asia/Irkutsk": {
    offset: "+08:00",
    city: "Irkutsk, Russia",
    value: "(GMT+08:00) Irkutsk, Russia",
  },
  "Asia/Kuala_Lumpur": {
    offset: "+08:00",
    city: "Kuala Lumpur, Malaysia",
    value: "(GMT+08:00) Kuala Lumpur, Malaysia",
  },
  "Asia/Kuching": {
    offset: "+08:00",
    city: "Kuching, Malaysia",
    value: "(GMT+08:00) Kuching, Malaysia",
  },
  "Asia/Macau": {
    offset: "+08:00",
    city: "Macau, Macau",
    value: "(GMT+08:00) Macau, Macau",
  },
  "Asia/Makassar": {
    offset: "+08:00",
    city: "Makassar, Indonesia",
    value: "(GMT+08:00) Makassar, Indonesia",
  },
  "Asia/Manila": {
    offset: "+08:00",
    city: "Manila, Philippines",
    value: "(GMT+08:00) Manila, Philippines",
  },
  "Asia/Shanghai": {
    offset: "+08:00",
    city: "Shanghai, China",
    value: "(GMT+08:00) Shanghai, China",
  },
  "Asia/Singapore": {
    offset: "+08:00",
    city: "Singapore, Singapore",
    value: "(GMT+08:00) Singapore, Singapore",
  },
  "Asia/Taipei": {
    offset: "+08:00",
    city: "Taipei, Taiwan",
    value: "(GMT+08:00) Taipei, Taiwan",
  },
  "Asia/Ulaanbaatar": {
    offset: "+08:00",
    city: "Ulaanbaatar, Mongolia",
    value: "(GMT+08:00) Ulaanbaatar, Mongolia",
  },
  "Australia/Perth": {
    offset: "+08:00",
    city: "Perth, Australia",
    value: "(GMT+08:00) Perth, Australia",
  },
  "Asia/Dili": {
    offset: "+09:00",
    city: "Dili, East Timor",
    value: "(GMT+09:00) Dili, East Timor",
  },
  "Asia/Jayapura": {
    offset: "+09:00",
    city: "Jayapura, Indonesia",
    value: "(GMT+09:00) Jayapura, Indonesia",
  },
  "Asia/Pyongyang": {
    offset: "+09:00",
    city: "Pyongyang, North Korea",
    value: "(GMT+09:00) Pyongyang, North Korea",
  },
  "Asia/Seoul": {
    offset: "+09:00",
    city: "Seoul, South Korea",
    value: "(GMT+09:00) Seoul, South Korea",
  },
  "Asia/Tokyo": {
    offset: "+09:00",
    city: "Tokyo, Japan",
    value: "(GMT+09:00) Tokyo, Japan",
  },
  "Asia/Yakutsk": {
    offset: "+09:00",
    city: "Yakutsk, Russia",
    value: "(GMT+09:00) Yakutsk, Russia",
  },
  "Pacific/Palau": {
    offset: "+09:00",
    city: "Palau, Palau",
    value: "(GMT+09:00) Palau, Palau",
  },
  "Pacific/West_Papua": {
    offset: "+09:00",
    city: "West Papua, Indonesia",
    value: "(GMT+09:00) West Papua, Indonesia",
  },
  "Australia/Adelaide": {
    offset: "+09:30",
    city: "Adelaide, Australia",
    value: "(GMT+09:30) Adelaide, Australia",
  },
  "Australia/Broken_Hill": {
    offset: "+09:30",
    city: "Broken Hill, Australia",
    value: "(GMT+09:30) Broken Hill, Australia",
  },
  "Australia/Darwin": {
    offset: "+09:30",
    city: "Darwin, Australia",
    value: "(GMT+09:30) Darwin, Australia",
  },
  "Asia/Vladivostok": {
    offset: "+10:00",
    city: "Vladivostok, Russia",
    value: "(GMT+10:00) Vladivostok, Russia",
  },
  "Australia/Brisbane": {
    offset: "+10:00",
    city: "Brisbane, Australia",
    value: "(GMT+10:00) Brisbane, Australia",
  },
  "Australia/Hobart": {
    offset: "+10:00",
    city: "Hobart, Australia",
    value: "(GMT+10:00) Hobart, Australia",
  },
  "Australia/Melbourne": {
    offset: "+10:00",
    city: "Melbourne, Australia",
    value: "(GMT+10:00) Melbourne, Australia",
  },
  "Australia/Sydney": {
    offset: "+10:00",
    city: "Sydney, Australia",
    value: "(GMT+10:00) Sydney, Australia",
  },
  "Pacific/Guam": {
    offset: "+10:00",
    city: "Guam, Guam",
    value: "(GMT+10:00) Guam, Guam",
  },
  "Pacific/Port_Moresby": {
    offset: "+10:00",
    city: "Port Moresby, Papua New Guinea",
    value: "(GMT+10:00) Port Moresby, Papua New Guinea",
  },
  "Pacific/Saipan": {
    offset: "+10:00",
    city: "Saipan, Northern Mariana Islands",
    value: "(GMT+10:00) Saipan, Northern Mariana Islands",
  },
  "Pacific/Truk": {
    offset: "+10:00",
    city: "Truk, Micronesia",
    value: "(GMT+10:00) Truk, Micronesia",
  },
  "Pacific/Chuuk": {
    offset: "+10:00",
    city: "Chuuk, Micronesia",
    value: "(GMT+10:00) Chuuk, Micronesia",
  },
  "Australia/Lord_Howe": {
    offset: "+10:30",
    city: "Lord Howe, Australia",
    value: "(GMT+10:30) Lord Howe, Australia",
  },
  "Asia/Magadan": {
    offset: "+11:00",
    city: "Magadan, Russia",
    value: "(GMT+11:00) Magadan, Russia",
  },
  "Asia/Sakhalin": {
    offset: "+11:00",
    city: "Sakhalin, Russia",
    value: "(GMT+11:00) Sakhalin, Russia",
  },
  "Pacific/Bougainville": {
    offset: "+11:00",
    city: "Bougainville, Papua New Guinea",
    value: "(GMT+11:00) Bougainville, Papua New Guinea",
  },
  "Pacific/Efate": {
    offset: "+11:00",
    city: "Efate, Vanuatu",
    value: "(GMT+11:00) Efate, Vanuatu",
  },
  "Pacific/Kosrae": {
    offset: "+11:00",
    city: "Kosrae, Micronesia",
    value: "(GMT+11:00) Kosrae, Micronesia",
  },
  "Pacific/Norfolk": {
    offset: "+11:00",
    city: "Norfolk Island, Australia",
    value: "(GMT+11:00) Norfolk Island, Australia",
  },
  "Pacific/Ponape": {
    offset: "+11:00",
    city: "Pohnpei, Micronesia",
    value: "(GMT+11:00) Pohnpei, Micronesia",
  },
  "Asia/Anadyr": {
    offset: "+12:00",
    city: "Anadyr, Russia",
    value: "(GMT+12:00) Anadyr, Russia",
  },
  "Asia/Kamchatka": {
    offset: "+12:00",
    city: "Kamchatka, Russia",
    value: "(GMT+12:00) Kamchatka, Russia",
  },
  "Pacific/Fiji": {
    offset: "+12:00",
    city: "Fiji, Fiji",
    value: "(GMT+12:00) Fiji, Fiji",
  },
  "Pacific/Funafuti": {
    offset: "+12:00",
    city: "Funafuti, Tuvalu",
    value: "(GMT+12:00) Funafuti, Tuvalu",
  },
  "Pacific/Marshall_Islands": {
    offset: "+12:00",
    city: "Marshall Islands, Marshall Islands",
    value: "(GMT+12:00) Marshall Islands, Marshall Islands",
  },
  "Pacific/Nauru": {
    offset: "+12:00",
    city: "Nauru, Nauru",
    value: "(GMT+12:00) Nauru, Nauru",
  },
  "Pacific/Tarawa": {
    offset: "+12:00",
    city: "Tarawa, Kiribati",
    value: "(GMT+12:00) Tarawa, Kiribati",
  },
  "Pacific/Wallis": {
    offset: "+12:00",
    city: "Wallis, Wallis and Futuna",
    value: "(GMT+12:00) Wallis, Wallis and Futuna",
  },
  "Pacific/Apia": {
    offset: "+13:00",
    city: "Apia, Samoa",
    value: "(GMT+13:00) Apia, Samoa",
  },
  "Pacific/Auckland": {
    offset: "+13:00",
    city: "Auckland, New Zealand",
    value: "(GMT+13:00) Auckland, New Zealand",
  },
  "Pacific/Tongatapu": {
    offset: "+13:00",
    city: "Tongatapu, Tonga",
    value: "(GMT+13:00) Tongatapu, Tonga",
  },
  "Pacific/Chatham": {
    offset: "+13:45",
    city: "Chatham Islands, New Zealand",
    value: "(GMT+13:45) Chatham Islands, New Zealand",
  },
  "Pacific/Kiritimati": {
    offset: "+14:00",
    city: "Kiritimati, Kiribati",
    value: "(GMT+14:00) Kiritimati, Kiribati",
  },
};

export const CURRENCY_LIST = [{
  "symbol": "$",
  "name": "US Dollar",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "USD",
  "name_plural": "US dollars"
}, {
  "symbol": "CA$",
  "name": "Canadian Dollar",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "CAD",
  "name_plural": "Canadian dollars"
}, {
  "symbol": "€",
  "name": "Euro",
  "symbol_native": "€",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "EUR",
  "name_plural": "euros"
}, {
  "symbol": "AED",
  "name": "United Arab Emirates Dirham",
  "symbol_native": "د.إ.‏",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "AED",
  "name_plural": "UAE dirhams"
}, {
  "symbol": "Af",
  "name": "Afghan Afghani",
  "symbol_native": "؋",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "AFN",
  "name_plural": "Afghan Afghanis"
}, {
  "symbol": "ALL",
  "name": "Albanian Lek",
  "symbol_native": "Lek",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "ALL",
  "name_plural": "Albanian lekë"
}, {
  "symbol": "AMD",
  "name": "Armenian Dram",
  "symbol_native": "դր.",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "AMD",
  "name_plural": "Armenian drams"
}, {
  "symbol": "AR$",
  "name": "Argentine Peso",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "ARS",
  "name_plural": "Argentine pesos"
}, {
  "symbol": "AU$",
  "name": "Australian Dollar",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "AUD",
  "name_plural": "Australian dollars"
}, {
  "symbol": "man.",
  "name": "Azerbaijani Manat",
  "symbol_native": "ман.",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "AZN",
  "name_plural": "Azerbaijani manats"
}, {
  "symbol": "KM",
  "name": "Bosnia-Herzegovina Convertible Mark",
  "symbol_native": "KM",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "BAM",
  "name_plural": "Bosnia-Herzegovina convertible marks"
}, {
  "symbol": "Tk",
  "name": "Bangladeshi Taka",
  "symbol_native": "৳",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "BDT",
  "name_plural": "Bangladeshi takas"
}, {
  "symbol": "BGN",
  "name": "Bulgarian Lev",
  "symbol_native": "лв.",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "BGN",
  "name_plural": "Bulgarian leva"
}, {
  "symbol": "BD",
  "name": "Bahraini Dinar",
  "symbol_native": "د.ب.‏",
  "decimal_digits": 3,
  "rounding": 0,
  "code": "BHD",
  "name_plural": "Bahraini dinars"
}, {
  "symbol": "FBu",
  "name": "Burundian Franc",
  "symbol_native": "FBu",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "BIF",
  "name_plural": "Burundian francs"
}, {
  "symbol": "BN$",
  "name": "Brunei Dollar",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "BND",
  "name_plural": "Brunei dollars"
}, {
  "symbol": "Bs",
  "name": "Bolivian Boliviano",
  "symbol_native": "Bs",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "BOB",
  "name_plural": "Bolivian bolivianos"
}, {
  "symbol": "R$",
  "name": "Brazilian Real",
  "symbol_native": "R$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "BRL",
  "name_plural": "Brazilian reals"
}, {
  "symbol": "BWP",
  "name": "Botswanan Pula",
  "symbol_native": "P",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "BWP",
  "name_plural": "Botswanan pulas"
}, {
  "symbol": "BYR",
  "name": "Belarusian Ruble",
  "symbol_native": "BYR",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "BYR",
  "name_plural": "Belarusian rubles"
}, {
  "symbol": "BZ$",
  "name": "Belize Dollar",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "BZD",
  "name_plural": "Belize dollars"
}, {
  "symbol": "CDF",
  "name": "Congolese Franc",
  "symbol_native": "FrCD",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "CDF",
  "name_plural": "Congolese francs"
}, {
  "symbol": "CHF",
  "name": "Swiss Franc",
  "symbol_native": "CHF",
  "decimal_digits": 2,
  "rounding": 0.05,
  "code": "CHF",
  "name_plural": "Swiss francs"
}, {
  "symbol": "CL$",
  "name": "Chilean Peso",
  "symbol_native": "$",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "CLP",
  "name_plural": "Chilean pesos"
}, {
  "symbol": "CN¥",
  "name": "Chinese Yuan",
  "symbol_native": "CN¥",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "CNY",
  "name_plural": "Chinese yuan"
}, {
  "symbol": "CO$",
  "name": "Colombian Peso",
  "symbol_native": "$",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "COP",
  "name_plural": "Colombian pesos"
}, {
  "symbol": "₡",
  "name": "Costa Rican Colón",
  "symbol_native": "₡",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "CRC",
  "name_plural": "Costa Rican colóns"
}, {
  "symbol": "CV$",
  "name": "Cape Verdean Escudo",
  "symbol_native": "CV$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "CVE",
  "name_plural": "Cape Verdean escudos"
}, {
  "symbol": "Kč",
  "name": "Czech Republic Koruna",
  "symbol_native": "Kč",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "CZK",
  "name_plural": "Czech Republic korunas"
}, {
  "symbol": "Fdj",
  "name": "Djiboutian Franc",
  "symbol_native": "Fdj",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "DJF",
  "name_plural": "Djiboutian francs"
}, {
  "symbol": "Dkr",
  "name": "Danish Krone",
  "symbol_native": "kr",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "DKK",
  "name_plural": "Danish kroner"
}, {
  "symbol": "RD$",
  "name": "Dominican Peso",
  "symbol_native": "RD$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "DOP",
  "name_plural": "Dominican pesos"
}, {
  "symbol": "DA",
  "name": "Algerian Dinar",
  "symbol_native": "د.ج.‏",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "DZD",
  "name_plural": "Algerian dinars"
}, {
  "symbol": "Ekr",
  "name": "Estonian Kroon",
  "symbol_native": "kr",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "EEK",
  "name_plural": "Estonian kroons"
}, {
  "symbol": "EGP",
  "name": "Egyptian Pound",
  "symbol_native": "ج.م.‏",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "EGP",
  "name_plural": "Egyptian pounds"
}, {
  "symbol": "Nfk",
  "name": "Eritrean Nakfa",
  "symbol_native": "Nfk",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "ERN",
  "name_plural": "Eritrean nakfas"
}, {
  "symbol": "Br",
  "name": "Ethiopian Birr",
  "symbol_native": "Br",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "ETB",
  "name_plural": "Ethiopian birrs"
}, {
  "symbol": "£",
  "name": "British Pound Sterling",
  "symbol_native": "£",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "GBP",
  "name_plural": "British pounds sterling"
}, {
  "symbol": "GEL",
  "name": "Georgian Lari",
  "symbol_native": "GEL",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "GEL",
  "name_plural": "Georgian laris"
}, {
  "symbol": "GH₵",
  "name": "Ghanaian Cedi",
  "symbol_native": "GH₵",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "GHS",
  "name_plural": "Ghanaian cedis"
}, {
  "symbol": "FG",
  "name": "Guinean Franc",
  "symbol_native": "FG",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "GNF",
  "name_plural": "Guinean francs"
}, {
  "symbol": "GTQ",
  "name": "Guatemalan Quetzal",
  "symbol_native": "Q",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "GTQ",
  "name_plural": "Guatemalan quetzals"
}, {
  "symbol": "HK$",
  "name": "Hong Kong Dollar",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "HKD",
  "name_plural": "Hong Kong dollars"
}, {
  "symbol": "HNL",
  "name": "Honduran Lempira",
  "symbol_native": "L",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "HNL",
  "name_plural": "Honduran lempiras"
}, {
  "symbol": "kn",
  "name": "Croatian Kuna",
  "symbol_native": "kn",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "HRK",
  "name_plural": "Croatian kunas"
}, {
  "symbol": "Ft",
  "name": "Hungarian Forint",
  "symbol_native": "Ft",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "HUF",
  "name_plural": "Hungarian forints"
}, {
  "symbol": "Rp",
  "name": "Indonesian Rupiah",
  "symbol_native": "Rp",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "IDR",
  "name_plural": "Indonesian rupiahs"
}, {
  "symbol": "₪",
  "name": "Israeli New Sheqel",
  "symbol_native": "₪",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "ILS",
  "name_plural": "Israeli new sheqels"
}, {
  "symbol": "Rs",
  "name": "Indian Rupee",
  "symbol_native": "টকা",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "INR",
  "name_plural": "Indian rupees"
}, {
  "symbol": "IQD",
  "name": "Iraqi Dinar",
  "symbol_native": "د.ع.‏",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "IQD",
  "name_plural": "Iraqi dinars"
}, {
  "symbol": "IRR",
  "name": "Iranian Rial",
  "symbol_native": "﷼",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "IRR",
  "name_plural": "Iranian rials"
}, {
  "symbol": "Ikr",
  "name": "Icelandic Króna",
  "symbol_native": "kr",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "ISK",
  "name_plural": "Icelandic krónur"
}, {
  "symbol": "J$",
  "name": "Jamaican Dollar",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "JMD",
  "name_plural": "Jamaican dollars"
}, {
  "symbol": "JD",
  "name": "Jordanian Dinar",
  "symbol_native": "د.أ.‏",
  "decimal_digits": 3,
  "rounding": 0,
  "code": "JOD",
  "name_plural": "Jordanian dinars"
}, {
  "symbol": "¥",
  "name": "Japanese Yen",
  "symbol_native": "￥",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "JPY",
  "name_plural": "Japanese yen"
}, {
  "symbol": "Ksh",
  "name": "Kenyan Shilling",
  "symbol_native": "Ksh",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "KES",
  "name_plural": "Kenyan shillings"
}, {
  "symbol": "KHR",
  "name": "Cambodian Riel",
  "symbol_native": "៛",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "KHR",
  "name_plural": "Cambodian riels"
}, {
  "symbol": "CF",
  "name": "Comorian Franc",
  "symbol_native": "FC",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "KMF",
  "name_plural": "Comorian francs"
}, {
  "symbol": "₩",
  "name": "South Korean Won",
  "symbol_native": "₩",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "KRW",
  "name_plural": "South Korean won"
}, {
  "symbol": "KD",
  "name": "Kuwaiti Dinar",
  "symbol_native": "د.ك.‏",
  "decimal_digits": 3,
  "rounding": 0,
  "code": "KWD",
  "name_plural": "Kuwaiti dinars"
}, {
  "symbol": "KZT",
  "name": "Kazakhstani Tenge",
  "symbol_native": "тңг.",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "KZT",
  "name_plural": "Kazakhstani tenges"
}, {
  "symbol": "LB£",
  "name": "Lebanese Pound",
  "symbol_native": "ل.ل.‏",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "LBP",
  "name_plural": "Lebanese pounds"
}, {
  "symbol": "SLRs",
  "name": "Sri Lankan Rupee",
  "symbol_native": "SL Re",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "LKR",
  "name_plural": "Sri Lankan rupees"
}, {
  "symbol": "Lt",
  "name": "Lithuanian Litas",
  "symbol_native": "Lt",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "LTL",
  "name_plural": "Lithuanian litai"
}, {
  "symbol": "Ls",
  "name": "Latvian Lats",
  "symbol_native": "Ls",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "LVL",
  "name_plural": "Latvian lati"
}, {
  "symbol": "LD",
  "name": "Libyan Dinar",
  "symbol_native": "د.ل.‏",
  "decimal_digits": 3,
  "rounding": 0,
  "code": "LYD",
  "name_plural": "Libyan dinars"
}, {
  "symbol": "MAD",
  "name": "Moroccan Dirham",
  "symbol_native": "د.م.‏",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "MAD",
  "name_plural": "Moroccan dirhams"
}, {
  "symbol": "MDL",
  "name": "Moldovan Leu",
  "symbol_native": "MDL",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "MDL",
  "name_plural": "Moldovan lei"
}, {
  "symbol": "MGA",
  "name": "Malagasy Ariary",
  "symbol_native": "MGA",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "MGA",
  "name_plural": "Malagasy Ariaries"
}, {
  "symbol": "MKD",
  "name": "Macedonian Denar",
  "symbol_native": "MKD",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "MKD",
  "name_plural": "Macedonian denari"
}, {
  "symbol": "MMK",
  "name": "Myanma Kyat",
  "symbol_native": "K",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "MMK",
  "name_plural": "Myanma kyats"
}, {
  "symbol": "MOP$",
  "name": "Macanese Pataca",
  "symbol_native": "MOP$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "MOP",
  "name_plural": "Macanese patacas"
}, {
  "symbol": "MURs",
  "name": "Mauritian Rupee",
  "symbol_native": "MURs",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "MUR",
  "name_plural": "Mauritian rupees"
}, {
  "symbol": "MX$",
  "name": "Mexican Peso",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "MXN",
  "name_plural": "Mexican pesos"
}, {
  "symbol": "RM",
  "name": "Malaysian Ringgit",
  "symbol_native": "RM",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "MYR",
  "name_plural": "Malaysian ringgits"
}, {
  "symbol": "MTn",
  "name": "Mozambican Metical",
  "symbol_native": "MTn",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "MZN",
  "name_plural": "Mozambican meticals"
}, {
  "symbol": "N$",
  "name": "Namibian Dollar",
  "symbol_native": "N$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "NAD",
  "name_plural": "Namibian dollars"
}, {
  "symbol": "₦",
  "name": "Nigerian Naira",
  "symbol_native": "₦",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "NGN",
  "name_plural": "Nigerian nairas"
}, {
  "symbol": "C$",
  "name": "Nicaraguan Córdoba",
  "symbol_native": "C$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "NIO",
  "name_plural": "Nicaraguan córdobas"
}, {
  "symbol": "Nkr",
  "name": "Norwegian Krone",
  "symbol_native": "kr",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "NOK",
  "name_plural": "Norwegian kroner"
}, {
  "symbol": "NPRs",
  "name": "Nepalese Rupee",
  "symbol_native": "नेरू",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "NPR",
  "name_plural": "Nepalese rupees"
}, {
  "symbol": "NZ$",
  "name": "New Zealand Dollar",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "NZD",
  "name_plural": "New Zealand dollars"
}, {
  "symbol": "OMR",
  "name": "Omani Rial",
  "symbol_native": "ر.ع.‏",
  "decimal_digits": 3,
  "rounding": 0,
  "code": "OMR",
  "name_plural": "Omani rials"
}, {
  "symbol": "B/.",
  "name": "Panamanian Balboa",
  "symbol_native": "B/.",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "PAB",
  "name_plural": "Panamanian balboas"
}, {
  "symbol": "S/.",
  "name": "Peruvian Nuevo Sol",
  "symbol_native": "S/.",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "PEN",
  "name_plural": "Peruvian nuevos soles"
}, {
  "symbol": "₱",
  "name": "Philippine Peso",
  "symbol_native": "₱",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "PHP",
  "name_plural": "Philippine pesos"
}, {
  "symbol": "PKRs",
  "name": "Pakistani Rupee",
  "symbol_native": "₨",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "PKR",
  "name_plural": "Pakistani rupees"
}, {
  "symbol": "zł",
  "name": "Polish Zloty",
  "symbol_native": "zł",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "PLN",
  "name_plural": "Polish zlotys"
}, {
  "symbol": "₲",
  "name": "Paraguayan Guarani",
  "symbol_native": "₲",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "PYG",
  "name_plural": "Paraguayan guaranis"
}, {
  "symbol": "QR",
  "name": "Qatari Rial",
  "symbol_native": "ر.ق.‏",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "QAR",
  "name_plural": "Qatari rials"
}, {
  "symbol": "RON",
  "name": "Romanian Leu",
  "symbol_native": "RON",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "RON",
  "name_plural": "Romanian lei"
}, {
  "symbol": "din.",
  "name": "Serbian Dinar",
  "symbol_native": "дин.",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "RSD",
  "name_plural": "Serbian dinars"
}, {
  "symbol": "RUB",
  "name": "Russian Ruble",
  "symbol_native": "руб.",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "RUB",
  "name_plural": "Russian rubles"
}, {
  "symbol": "RWF",
  "name": "Rwandan Franc",
  "symbol_native": "FR",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "RWF",
  "name_plural": "Rwandan francs"
}, {
  "symbol": "SR",
  "name": "Saudi Riyal",
  "symbol_native": "ر.س.‏",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "SAR",
  "name_plural": "Saudi riyals"
}, {
  "symbol": "SDG",
  "name": "Sudanese Pound",
  "symbol_native": "SDG",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "SDG",
  "name_plural": "Sudanese pounds"
}, {
  "symbol": "Skr",
  "name": "Swedish Krona",
  "symbol_native": "kr",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "SEK",
  "name_plural": "Swedish kronor"
}, {
  "symbol": "S$",
  "name": "Singapore Dollar",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "SGD",
  "name_plural": "Singapore dollars"
}, {
  "symbol": "Ssh",
  "name": "Somali Shilling",
  "symbol_native": "Ssh",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "SOS",
  "name_plural": "Somali shillings"
}, {
  "symbol": "SY£",
  "name": "Syrian Pound",
  "symbol_native": "ل.س.‏",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "SYP",
  "name_plural": "Syrian pounds"
}, {
  "symbol": "฿",
  "name": "Thai Baht",
  "symbol_native": "฿",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "THB",
  "name_plural": "Thai baht"
}, {
  "symbol": "DT",
  "name": "Tunisian Dinar",
  "symbol_native": "د.ت.‏",
  "decimal_digits": 3,
  "rounding": 0,
  "code": "TND",
  "name_plural": "Tunisian dinars"
}, {
  "symbol": "T$",
  "name": "Tongan Paʻanga",
  "symbol_native": "T$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "TOP",
  "name_plural": "Tongan paʻanga"
}, {
  "symbol": "TL",
  "name": "Turkish Lira",
  "symbol_native": "TL",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "TRY",
  "name_plural": "Turkish Lira"
}, {
  "symbol": "TT$",
  "name": "Trinidad and Tobago Dollar",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "TTD",
  "name_plural": "Trinidad and Tobago dollars"
}, {
  "symbol": "NT$",
  "name": "New Taiwan Dollar",
  "symbol_native": "NT$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "TWD",
  "name_plural": "New Taiwan dollars"
}, {
  "symbol": "TSh",
  "name": "Tanzanian Shilling",
  "symbol_native": "TSh",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "TZS",
  "name_plural": "Tanzanian shillings"
}, {
  "symbol": "₴",
  "name": "Ukrainian Hryvnia",
  "symbol_native": "₴",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "UAH",
  "name_plural": "Ukrainian hryvnias"
}, {
  "symbol": "USh",
  "name": "Ugandan Shilling",
  "symbol_native": "USh",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "UGX",
  "name_plural": "Ugandan shillings"
}, {
  "symbol": "$U",
  "name": "Uruguayan Peso",
  "symbol_native": "$",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "UYU",
  "name_plural": "Uruguayan pesos"
}, {
  "symbol": "UZS",
  "name": "Uzbekistan Som",
  "symbol_native": "UZS",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "UZS",
  "name_plural": "Uzbekistan som"
}, {
  "symbol": "Bs.F.",
  "name": "Venezuelan Bolívar",
  "symbol_native": "Bs.F.",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "VEF",
  "name_plural": "Venezuelan bolívars"
}, {
  "symbol": "đ",
  "name": "Vietnamese Dong",
  "symbol_native": "₫",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "VND",
  "name_plural": "Vietnamese dong"
}, {
  "symbol": "FCFA",
  "name": "CFA Franc BEAC",
  "symbol_native": "FCFA",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "XAF",
  "name_plural": "CFA francs BEAC"
}, {
  "symbol": "CFA",
  "name": "CFA Franc BCEAO",
  "symbol_native": "CFA",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "XOF",
  "name_plural": "CFA francs BCEAO"
}, {
  "symbol": "YR",
  "name": "Yemeni Rial",
  "symbol_native": "ر.ي.‏",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "YER",
  "name_plural": "Yemeni rials"
}, {
  "symbol": "R",
  "name": "South African Rand",
  "symbol_native": "R",
  "decimal_digits": 2,
  "rounding": 0,
  "code": "ZAR",
  "name_plural": "South African rand"
}, {
  "symbol": "ZK",
  "name": "Zambian Kwacha",
  "symbol_native": "ZK",
  "decimal_digits": 0,
  "rounding": 0,
  "code": "ZMK",
  "name_plural": "Zambian kwachas"
}];

export const WEEK_DAYS = [
  { label: "Monday", value: "monday" },
  { label: "Tuesday", value: "tuesday" },
  { label: "Wednesday", value: "wednesday" },
  { label: "Thursday", value: "thursday" },
  { label: "Friday", value: "friday" },
  { label: "Saturday", value: "saturday" },
  { label: "Sunday", value: "sunday" },
];

export const QR_CODE_TYPES = [
  { label: "Generate code", value: QRCodeType.GENERATE },
  { label: "Import code", value: QRCodeType.IMPORT },
];

export const SPECIAL_TYPES = [
  { label: i18n.t("select.specialType.hot"), value: "hot" },
  { label: i18n.t("select.specialType.discount"), value: "discount" },
  { label: i18n.t("select.specialType.recommend"), value: "recommend" },
];

export const IS_SHOW_BIB_LIST = [
  { label: i18n.t("select.isShowBIB.showBIB"), value: true },
  { label: i18n.t("select.isShowBIB.hideBIB"), value: false },
]

export const ATTRACTION_QR_CODE_SENDING_STATUS = [
  { label: "Created", value: AttractionQRCodeSendingStatus.CREATED },
  { label: "Waiting", value: AttractionQRCodeSendingStatus.WAITING },
  { label: "Sent", value: AttractionQRCodeSendingStatus.SENT },
];

export const TICKET_PLATFORMS = [
  { label: "ActiUp platform", value: "actiup" },
  { label: "Partner’s platform", value: "partner" },
];

export const CONTENT_LANGUAGES = [
  {
    label: "English",
    value: SupportedLanguage.EN,
    icon: "/images/flags/circle-en.png",
  },
  {
    label: "Vietnamese",
    value: SupportedLanguage.VI,
    icon: "/images/flags/circle-vi.png",
  },
];

export const LANGUAGES = [
  {
    label: i18n.t("select.language.english"),
    value: SupportedLanguage.EN,
    icon: "/images/flags/circle-en.png",
  },
  {
    label: i18n.t("select.language.vietnamese"),
    value: SupportedLanguage.VI,
    icon: "/images/flags/circle-vi.png",
  },
];

export const SELECT_TICKET_PLATFORMS = [
  {
    label: "Sell ticket on ActiUp",
    value: TicketPlatform.ACTIUP,
  },
  {
    label: "Sell ticket on organizer’s platform",
    value: TicketPlatform.WHITELABEL,
  },
];


export const CREATE_EVENT_STEPS = [
  {
    title: i18n.t("eventSetup.step.eventInfo"),
    subtitle: "Add event information for participants to grasp the details.",
    number: CreateEventStep.BUILD_EVENT_PAGE,
  },
  {
    title: i18n.t("eventSetup.step.participantTemplate"),
    subtitle: "Add information about your registration form in event",
    number: CreateEventStep.PARTICIPANT_TEMPLATE,
  },
  {
    title: i18n.t("eventSetup.step.phaseAndTicket"),
    subtitle: "Setup your tickets to sell",
    number: CreateEventStep.CREATE_TICKET_PHASE,
  },
  {
    title: i18n.t("eventSetup.step.additionalSetup"),
    subtitle: "Set up more information for your event",
    number: CreateEventStep.ADDITIONAL_SETUP,
  },
];

export const ALL_OPTION = { label: "All", value: "all" };

export const DEFAULT_ATTRACTION_TEMPLATE_FIELDS: IAttendantTemplateItem["data"] =
  [
    {
      group: {
        group_key: "personal_information",
        label: { en: "Personal Information", vi: "Thông tin cá nhân" },
      },
      fields: [
        TEXT_FIELDS.first_name,
        TEXT_FIELDS.last_name,
        TEXT_FIELDS.phone,
        TEXT_FIELDS.email,
      ],
    },
    {
      group: {
        group_key: "additional_information",
        label: { en: "Additional Information", vi: "Thông tin bổ sung" },
      },
      fields: [],
    },
    {
      group: {
        group_key: "medical_information",
        label: { en: "Medical Information", vi: "Thông tin y tế" },
      },
      fields: [],
    },
  ];

export const DEFAULT_TEMPLATE_FIELDS: IAttendantTemplateItem["data"] = [
  {
    group: {
      group_key: "personal_information",
      label: { en: "Personal Information", vi: "Thông tin cá nhân" },
    },
    fields: [
      TEXT_FIELDS.first_name,
      TEXT_FIELDS.last_name,
      TEXT_FIELDS.phone,
      TEXT_FIELDS.email,
      TEXT_FIELDS.id_card,
      SELECT_FIELDS.nationality,
      SELECT_FIELDS.country,
      SELECT_FIELDS.province_living,
      SELECT_FIELDS.gender,
      DATE_FIELDS.dob,
      TEXT_FIELDS.legal_full_name,
      TEXT_FIELDS.legal_id_card,
      DATE_FIELDS.legal_birthday,
      TEXT_FIELDS.legal_email,
      SELECT_FIELDS.runner_relationship,
    ],
  },
  {
    group: {
      group_key: "additional_information",
      label: { en: "Additional Information", vi: "Thông tin bổ sung" },
    },
    fields: [
      TEXT_FIELDS.name_on_bib,
      SELECT_FIELDS.t_shirt_size,
      SELECT_FIELDS.t_shirt_size_finisher,
      TEXT_FIELDS.emergency_contact_name,
      TEXT_FIELDS.emergency_contact_phone,
      SELECT_FIELDS.club,
    ],
  },
  {
    group: {
      group_key: "medical_information",
      label: { en: "Medical Information", vi: "Thông tin y tế" },
    },
    fields: [
      SELECT_FIELDS.blood_type,
      TEXT_FIELDS.allergy,
      TEXT_FIELDS.medicine,
    ],
  },
  {
    group: {
      group_key: "other_information",
      label: { en: "Other Information", vi: "Thông tin khác" },
    },
    fields: [],
  }
];

export const DEFAULT_PERMISSIONS = [
  {
    id: 'dashboard',
    name: 'dashboard',
    resource: "",
    app: "dashboard",
    actions: [
      {
        id: 'all',
        label: 'select-all',
        selected: false,
        disable: true
      },
      {
        id: 'view',
        label: 'view',
        selected: false,
        disable: true
      },
      {
        id: 'edit',
        label: 'edit',
        selected: false,
        disable: true
      },
      {
        id: 'create',
        label: 'create',
        selected: false,
        disable: true
      },
    ],
    child: false,
    parent: '',
    icon: '/images/icons/icon-dashboard.svg',
    disable: true
  },
    {
    id: 'events',
    name: 'menu-event',
    actions: [],
    child: false,
    parent: '',
    icon: '/images/icons/icon-organization.svg',
    disable: true
  },
  {
    id: 'event_sports',
    name: 'menu-event-sport',
    resource: "sports",
    app: "events",
    actions: [
      {
        id: 'all',
        label: 'select-all',
        selected: false,
        disable: true
      },
      {
        id: 'view',
        label: 'view',
        selected: false,
        disable: true
      },
      {
        id: 'edit',
        label: 'edit',
        selected: false,
        disable: true
      },
      {
        id: 'create',
        label: 'create',
        selected: false,
        disable: true
      },
    ],
    child: true,
    parent: 'events',
    icon: '',
    disable: true
  },
  {
    id: 'orders',
    name: 'orders',
    resource: "",
    app: "orders",
    actions: [
      {
        id: 'all',
        label: 'select-all',
        selected: false,
        disable: true
      },
      {
        id: 'view',
        label: 'view',
        selected: false,
        disable: true
      },
      {
        id: 'edit',
        label: 'edit',
        selected: false,
        disable: true
      },
      {
        id: 'create',
        label: 'create',
        selected: false,
        disable: true
      },
    ],
    child: false,
    parent: '',
    icon: '/images/icons/orders.svg',
    disable: true
  },
  {
    id: "attendees",
    name: "attendees",
    actions: [],
    child: false,
    parent: '',
    icon: '/images/icons/view-grid-add.svg',
    disable: true
  },
  {
    id: "sports",
    name: "sport-attendees",
    resource: "sports",
    app: "attendees",
    actions: [
      {
        id: 'all',
        label: 'select-all',
        selected: false,
        disable: true
      },
      {
        id: 'view',
        label: 'view',
        selected: false,
        disable: true
      },
      {
        id: 'edit',
        label: 'edit',
        selected: false,
        disable: true
      },
      {
        id: 'create',
        label: 'create',
        selected: false,
        disable: true,
      },
    ],
    child: true,
    parent: 'attendees',
    icon: '',
    disable: true
  },
  {
    id: "attractions",
    name: "attraction-attendees",
    resource: "attractions",
    app: "attendees",
    actions: [
      {
        id: 'all',
        label: 'select-all',
        selected: false,
        disable: true
      },
      {
        id: 'view',
        label: 'view',
        selected: false,
        disable: true
      },
      {
        id: 'edit',
        label: 'edit',
        selected: false,
        disable: true
      },
      {
        id: 'create',
        label: 'create',
        selected: false,
        disable: true
      },
    ],
    child: true,
    parent: 'attendees',
    icon: '',
    disable: true
  },
  {
    id: 'organization',
    name: 'organization',
    actions: [],
    child: false,
    parent: '',
    icon: '/images/icons/icon-organization.svg',
    disable: true
  },
  {
    id: 'info',
    name: 'information',
    resource: "info",
    app: "organization",
    actions: [
      {
        id: 'all',
        label: 'select-all',
        selected: false,
        disable: true
      },
      {
        id: 'view',
        label: 'view',
        selected: false,
        disable: true
      },
      {
        id: 'edit',
        label: 'edit',
        selected: false,
        disable: true
      },
      {
        id: 'create',
        label: 'create',
        selected: false,
        disable: true
      },
    ],
    child: true,
    parent: 'organization',
    icon: '',
    disable: true
  },
  {
    id: 'role',
    name: 'role-management',
    resource: "role",
    app: "organization",
    actions: [
      {
        id: 'all',
        label: 'select-all',
        selected: false,
        disable: true
      },
      {
        id: 'view',
        label: 'view',
        selected: false,
        disable: true
      },
      {
        id: 'edit',
        label: 'edit',
        selected: false,
        disable: true
      },
      {
        id: 'create',
        label: 'create',
        selected: false,
        disable: true
      },
    ],
    child: true,
    parent: 'organization',
    icon: '',
    disable: true
  },
  {
    id: 'account',
    name: 'account-management',
    resource: "account",
    app: "organization",
    actions: [
      {
        id: 'all',
        label: 'select-all',
        selected: false,
        disable: true
      },
      {
        id: 'view',
        label: 'view',
        selected: false,
        disable: true
      },
      {
        id: 'edit',
        label: 'edit',
        selected: false,
        disable: true
      },
      {
        id: 'create',
        label: 'create',
        selected: false,
        disable: true
      },
    ],
    child: true,
    parent: 'organization',
    icon: '',
    disable: true
  },
  // {
  //   id: 'coupon',
  //   name: 'coupon',
  //   resource: "",
  //   app: "coupon",
  //   actions: [
  //     {
  //       id: 'all',
  //       label: 'select-all',
  //       selected: false,
  //       disable: true
  //     },
  //     {
  //       id: 'view',
  //       label: 'view',
  //       selected: false,
  //       disable: true
  //     },
  //     {
  //       id: 'edit',
  //       label: 'edit',
  //       selected: false,
  //       disable: true
  //     },
  //     {
  //       id: 'create',
  //       label: 'create',
  //       selected: false,
  //       disable: true
  //     },
  //   ],
  //   child: false,
  //   parent: '',
  //   icon: '/images/icons/coupon.svg',
  //   disable: true
  // },
  {
    id: 'promotion',
    name: 'menu-promotion',
    actions: [],
    child: false,
    parent: '',
    icon: '/images/icons/icon-organization.svg',
    disable: true
  },
  {
    id: 'coupon',
    name: 'promotion-coupon-management',
    resource: "coupon",
    app: "promotion",
    actions: [
      {
        id: 'all',
        label: 'select-all',
        selected: false,
        disable: true
      },
      {
        id: 'view',
        label: 'view',
        selected: false,
        disable: true
      },
      {
        id: 'edit',
        label: 'edit',
        selected: false,
        disable: true
      },
      {
        id: 'create',
        label: 'create',
        selected: false,
        disable: true
      },
      {
        id: 'export',
        label: 'export',
        selected: false,
        disable: true
      },
    ],
    child: true,
    parent: 'promotion',
    icon: '',
    disable: true
  },
  {
    id: 'condition',
    name: 'promotion-condition-management',
    resource: "condition",
    app: "promotion",
    actions: [
      {
        id: 'all',
        label: 'select-all',
        selected: false,
        disable: true
      },
      {
        id: 'view',
        label: 'view',
        selected: false,
        disable: true
      },
      {
        id: 'edit',
        label: 'edit',
        selected: false,
        disable: true
      },
      {
        id: 'create',
        label: 'create',
        selected: false,
        disable: true
      },
    ],
    child: true,
    parent: 'promotion',
    icon: '',
    disable: true
  },
];
